import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import DOMPurify from "dompurify";
import axios from "axios";
import JSZip from "jszip";
import logo from '../assets/CAROLINA.png';
import templateFile from '../assets/OMF_GEN_V3.docx';
import { jsPDF } from "jspdf";
import { BsArrowLeft, BsFillMoonFill, BsFillSunFill, BsEye, BsEyeSlash, BsPencil, BsCheck2, BsXLg, BsFileEarmarkPdf, BsImage, BsTrash, BsGripVertical, BsPlusCircle, BsXCircle, BsTypeBold, BsTypeItalic, BsTypeUnderline, BsFileEarmarkText, BsClipboard, BsArrowCounterclockwise, BsArrowClockwise, BsListUl, BsListOl, BsTextIndentLeft, BsTextIndentRight, BsChevronDown, BsChevronRight, BsZoomIn, BsZoomOut, BsMic, BsMicFill } from "react-icons/bs";
import "./TemplateCustomizer.css";

import { Amplify } from 'aws-amplify';
import { getCurrentUser, signOut, fetchAuthSession } from 'aws-amplify/auth';
import outputs from '../amplify_outputs.json';
import { API_URLS } from '../config/api';
import '@aws-amplify/ui-react/styles.css';
Amplify.configure(outputs);

const SETTINGS_URL = API_URLS.SETTINGS;

function parseDocumentXml(xmlString) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xmlString, "application/xml");
    const ns = "http://schemas.openxmlformats.org/wordprocessingml/2006/main";
    const paragraphs = doc.getElementsByTagNameNS(ns, "p");

    const sections = [];
    let currentSection = null;
    let headerParagraphs = [];

    for (let i = 0; i < paragraphs.length; i++) {
        const p = paragraphs[i];
        const runs = p.getElementsByTagNameNS(ns, "r");
        let text = "";
        let isBold = false;

        for (let r = 0; r < runs.length; r++) {
            const run = runs[r];
            const rPr = run.getElementsByTagNameNS(ns, "rPr")[0];
            if (rPr && rPr.getElementsByTagNameNS(ns, "b").length > 0) {
                isBold = true;
            }
            const tNodes = run.getElementsByTagNameNS(ns, "t");
            for (let t = 0; t < tNodes.length; t++) {
                text += tNodes[t].textContent || "";
            }
        }

        text = text.trim();
        if (!text) continue;

        if (text === "REPORT INFO:" || text.startsWith("Doctor:") || text.startsWith("Email:") ||
            text.startsWith("Scan Date:") || text.startsWith("Patient:") || text.startsWith("DOB:") ||
            text.startsWith("Report Date:")) {
            headerParagraphs.push(text);
            continue;
        }

        if (text === "OMF RADIOLOGY REPORT") continue;
        if (text.startsWith("Please contact")) continue;
        if (text.startsWith("Sincerely")) continue;
        if (text.startsWith("+1 (")) continue;
        if (text.startsWith("www.")) continue;
        if (text === "IMAGES:") continue;
        if (text.startsWith("_____")) continue;
        if (text.startsWith("Key -")) continue;

        if (isBold && text.endsWith(":")) {
            if (currentSection) {
                sections.push(currentSection);
            }
            currentSection = {
                id: sections.length,
                title: text.replace(/:$/, ""),
                content: [],
                images: [],
                enabled: true,
            };
        } else if (currentSection) {
            currentSection.content.push(text);
        } else {
            sections.push({
                id: sections.length,
                title: "Introduction",
                content: [text],
                images: [],
                enabled: true,
            });
        }
    }

    if (currentSection) {
        sections.push(currentSection);
    }

    return sections;
}


// Serialize a section for persistence. Images are patient-specific and are
// dropped, but the Images section's subsection names are kept (as empty
// arrays) so the template's structure survives a reload.
function serializeSection(s) {
    const base = { id: s.id, title: s.title, content: s.content, enabled: s.enabled };
    if (s.imageSubsections) {
        base.imageSubsections = Object.keys(s.imageSubsections).reduce((acc, k) => {
            acc[k] = [];
            return acc;
        }, {});
    }
    return base;
}

function TemplateCustomizer() {
    const navigate = useNavigate();
    const location = useLocation();
    const patient = location.state?.patient || {};
    const [colorMode, setColorMode] = useState(false);
    const [sections, setSections] = useState([]);
    const [allSections, setAllSections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [settingsLoading, setSettingsLoading] = useState(true);
    const [editingSection, setEditingSection] = useState(null);
    const [editText, setEditText] = useState("");
    const [userAuthStatus, setUserAuthStatus] = useState('');
    const [headerLogoBase64, setHeaderLogoBase64] = useState(null);
    const [signatureBase64, setSignatureBase64] = useState(null);
    const [activeTemplate, setActiveTemplate] = useState('general');
    const customTemplatesRef = useRef([]);
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);
    const [editingTitle, setEditingTitle] = useState(null);
    const [clipboardPopout, setClipboardPopout] = useState(null);
    const [editingSnippetId, setEditingSnippetId] = useState(null);
    const [editTitleText, setEditTitleText] = useState("");
    const [quickTextOpen, setQuickTextOpen] = useState(false);
    const [quickSnippets, setQuickSnippets] = useState([]);
    const [sectionSnippets, setSectionSnippets] = useState({});
    const [customTemplates, setCustomTemplates] = useState([]);
    const [generalTemplateSaved, setGeneralTemplateSaved] = useState(null);
    const [generalTemplateName, setGeneralTemplateName] = useState('General Template');
    const [snippetForm, setSnippetForm] = useState(null);
    const [sectionSnippetForm, setSectionSnippetForm] = useState(null);
    const [recordBannerVisible, setRecordBannerVisible] = useState(true);
    const [savingReport, setSavingReport] = useState(false);
    const [loadingSavedReport, setLoadingSavedReport] = useState(false);
    const [sectionsNonce, setSectionsNonce] = useState(0);
    const [reportLoaded, setReportLoaded] = useState(false);
    const [imageBank, setImageBank] = useState([]);
    const imageBankInputRef = useRef(null);
    const [quickPanelTab, setQuickPanelTab] = useState('snippets');
    const [collapsedCategories, setCollapsedCategories] = useState({});
    const [editingSubsection, setEditingSubsection] = useState(null);
    const [editSubsectionText, setEditSubsectionText] = useState("");
    const [lightbox, setLightbox] = useState(null);
    const [lightboxZoom, setLightboxZoom] = useState(1);
    const [lightboxPan, setLightboxPan] = useState({ x: 0, y: 0 });
    const [imageMasterCollapsed, setImageMasterCollapsed] = useState(false);
    const lightboxStageRef = useRef(null);
    const lightboxDragRef = useRef(null);
    const lastFocusedSectionRef = useRef(null);

    useEffect(() => {
        customTemplatesRef.current = customTemplates;
    }, [customTemplates]);

    useEffect(() => {
        const checkAuth = async () => {
            try {
                const session = await fetchAuthSession();
                if (!session.tokens?.idToken) {
                    navigate('/sign-in');
                    return;
                }
                const user = await getCurrentUser();
                setUserAuthStatus(user.userId);

                // Load saved templates and snippets from DynamoDB
                try {
                    setSettingsLoading(true);
                    const res = await axios.post(SETTINGS_URL, { body: { operation: 'getAll', userId: user.userId } });
                    const data = typeof res.data.body === 'string' ? JSON.parse(res.data.body) : res.data.body || res.data;
                    if (data.generalTemplate) {
                        setGeneralTemplateSaved(data.generalTemplate);
                        if (data.generalTemplate.name) setGeneralTemplateName(data.generalTemplate.name);
                    }
                    if (data.templates && data.templates.length > 0) {
                        setCustomTemplates(data.templates);
                    }
                    if (data.snippets && data.snippets.length > 0) {
                        setQuickSnippets(data.snippets);
                    }
                    if (data.sectionSnippets) {
                        setSectionSnippets(data.sectionSnippets);
                    }
                } catch (err) {
                    console.error('Failed to load settings from DynamoDB:', err);
                } finally {
                    setSettingsLoading(false);
                }
            } catch {
                navigate('/sign-in');
            }
        };
        checkAuth();
    }, [navigate]);

    useEffect(() => {
        const saved = localStorage.getItem('colorMode');
        if (saved !== null) setColorMode(saved === 'true');
    }, []);

    useEffect(() => {
        const loadSignature = async () => {
            try {
                const res = await fetch(process.env.PUBLIC_URL + '/signature.png');
                if (!res.ok) return;
                const blob = await res.blob();
                const reader = new FileReader();
                reader.onloadend = () => setSignatureBase64(reader.result);
                reader.readAsDataURL(blob);
            } catch (e) {}
        };
        loadSignature();
    }, []);

    useEffect(() => {
        const style = document.createElement('style');
        style.setAttribute('data-template-scroll', '');
        style.textContent = `
            html, body, #root, #body, #body .page, .page {
                overflow: visible !important;
                height: auto !important;
            }
        `;
        document.head.appendChild(style);
        return () => { document.head.removeChild(style); };
    }, []);

    useEffect(() => {
        const loadTemplate = async () => {
            try {
                const response = await fetch(templateFile);
                if (!response.ok) {
                    throw new Error(`Failed to fetch template: ${response.status}`);
                }
                const blob = await response.blob();
                const zip = await JSZip.loadAsync(blob);

                const docFile = zip.file("word/document.xml");
                if (!docFile) {
                    throw new Error("Invalid template: missing document.xml");
                }

                const xml = await docFile.async("string");
                const parsed = parseDocumentXml(xml);

                if (!parsed.length) {
                    throw new Error("No sections found in template");
                }

                const populatedSections = parsed
                    .filter(s => !s.title.toUpperCase().includes("OBJECTIVE"))
                    .map(s => {
                        if (s.title.toLowerCase().includes('region') && patient.regions_of_interest) {
                            return { ...s, content: [patient.regions_of_interest] };
                        }
                        return s;
                    });

                // Insert "Images" section with subsections at the end if not present
                const hasImages = populatedSections.some(s => /^images$/i.test(s.title.trim()));
                if (!hasImages) {
                    populatedSections.push({
                        id: Date.now() + 999,
                        title: "Images",
                        content: [],
                        images: [],
                        enabled: true,
                        imageSubsections: {
                            Panoramic: [],
                            Airway: [],
                            TMJ: [],
                        },
                    });
                }

                setAllSections(populatedSections);
                setSections(populatedSections);

                const imgFile = zip.file("word/media/image1.png");
                if (imgFile) {
                    const imgData = await imgFile.async("base64");
                    setHeaderLogoBase64("data:image/png;base64," + imgData);
                }
            } catch (err) {
                setLoadError(err.message || "Failed to load template");
            } finally {
                setLoading(false);
            }
        };
        loadTemplate();
    }, []);

    useEffect(() => {
        if (!reportLoaded && generalTemplateSaved && allSections.length > 0 && activeTemplate === 'general') {
            setSections(prev => prev.map(s => {
                const saved = generalTemplateSaved.sections.find(gs => gs.id === s.id);
                if (saved) {
                    const next = { ...s, title: saved.title, content: saved.content, enabled: saved.enabled };
                    if (saved.imageSubsections) next.imageSubsections = saved.imageSubsections;
                    return next;
                }
                return s;
            }));
        }
    }, [generalTemplateSaved, allSections, reportLoaded]);

    const toggleSection = useCallback((id) => {
        setSections(prev => prev.map(s =>
            s.id === id ? { ...s, enabled: !s.enabled } : s
        ));
    }, []);

    const startEditing = useCallback((section) => {
        setEditingSection(section.id);
        setEditText(section.content.join("\n"));
    }, []);

    const saveEdit = useCallback((id) => {
        try {
            const html = editorRef.current?.innerHTML || editText;
            if (!html || !html.trim()) {
                alert("Section content cannot be empty.");
                return;
            }
            const lines = html
                .replace(/<div>/gi, '\n').replace(/<\/div>/gi, '')
                .replace(/<br\s*\/?>/gi, '\n')
                .split('\n')
                .map(l => l.trim())
                .filter(l => l);
            setSections(prev => prev.map(s =>
                s.id === id ? { ...s, content: lines } : s
            ));
            setEditingSection(null);
            setEditText("");
        } catch (err) {
            alert("Failed to save section content. Please try again.");
        }
    }, [editText]);

    const cancelEdit = useCallback(() => {
        setEditingSection(null);
        setEditText("");
    }, []);

    const insertSnippet = useCallback((html) => {
        document.execCommand('insertHTML', false, html);
    }, []);

    const addSnippet = useCallback(() => {
        setSnippetForm({ id: null, label: '', text: '' });
    }, []);

    const editSnippet = useCallback((id) => {
        const snippet = quickSnippets.find(s => s.id === id);
        if (snippet) setSnippetForm({ id, label: snippet.label, text: snippet.text });
    }, [quickSnippets]);

    const saveSnippetForm = useCallback(async () => {
        const snippetHtml = snippetEditorRef.current?.innerHTML || '';
        const hasContent = snippetHtml.replace(/<[^>]*>/g, '').trim().length > 0;
        if (!snippetForm || !snippetForm.label.trim() || !hasContent) return;
        const sanitizedText = DOMPurify.sanitize(snippetHtml);
        let updated;
        if (snippetForm.id) {
            updated = quickSnippets.map(s =>
                s.id === snippetForm.id ? { ...s, label: snippetForm.label.trim(), text: sanitizedText } : s
            );
        } else {
            updated = [...quickSnippets, { id: Date.now(), label: snippetForm.label.trim(), text: sanitizedText }];
        }
        setQuickSnippets(updated);
        setSnippetForm(null);
        if (userAuthStatus) {
            try { await axios.post(SETTINGS_URL, { body: { operation: 'saveSnippets', userId: userAuthStatus, snippets: updated } }); } catch (err) { console.error("Failed to save snippets:", err); }
        }
    }, [snippetForm, userAuthStatus, quickSnippets]);

    const removeSnippet = useCallback(async (id) => {
        const updated = quickSnippets.filter(s => s.id !== id);
        setQuickSnippets(updated);
        if (userAuthStatus) {
            try { await axios.post(SETTINGS_URL, { body: { operation: 'saveSnippets', userId: userAuthStatus, snippets: updated } }); } catch (err) { console.error("Failed to save snippets:", err); }
        }
    }, [userAuthStatus, quickSnippets]);

    const saveSectionSnippets = useCallback(async (updatedMap) => {
        setSectionSnippets(updatedMap);
        if (userAuthStatus) {
            try { await axios.post(SETTINGS_URL, { body: { operation: 'saveSectionSnippets', userId: userAuthStatus, sectionSnippets: updatedMap } }); } catch (err) { console.error("Failed to save section snippets:", err); }
        }
    }, [userAuthStatus]);

    const addSectionSnippet = useCallback((sectionTitle) => {
        setSectionSnippetForm({ sectionTitle, id: null, label: '', category: '', objectiveText: '', impressionText: '' });
    }, []);

    const saveSectionSnippetForm = useCallback(async () => {
        if (!sectionSnippetForm || !sectionSnippetForm.label.trim()) return;
        const objectiveHtml = snippetEditorRef.current?.innerHTML || '';
        const impressionHtml = snippetImpressionRef.current?.innerHTML || '';
        const hasObjective = objectiveHtml.replace(/<[^>]*>/g, '').trim().length > 0;
        const hasImpression = impressionHtml.replace(/<[^>]*>/g, '').trim().length > 0;
        if (!hasObjective && !hasImpression) return;
        const sanitizedObjective = DOMPurify.sanitize(objectiveHtml);
        const sanitizedImpression = DOMPurify.sanitize(impressionHtml);
        const key = sectionSnippetForm.sectionTitle;
        const current = sectionSnippets[key] || [];
        let updated;
        const snippetData = { label: sectionSnippetForm.label.trim(), category: (sectionSnippetForm.category || '').trim(), objectiveText: sanitizedObjective, impressionText: sanitizedImpression, text: sanitizedObjective };
        if (sectionSnippetForm.id) {
            updated = current.map(s => s.id === sectionSnippetForm.id ? { ...s, ...snippetData } : s);
        } else {
            updated = [...current, { id: Date.now(), ...snippetData }];
        }
        const updatedMap = { ...sectionSnippets, [key]: updated };
        await saveSectionSnippets(updatedMap);
        setSectionSnippetForm(null);
    }, [sectionSnippetForm, sectionSnippets, saveSectionSnippets]);

    const insertSnippetDual = useCallback((snippet) => {
        // Insert objective text at cursor position
        if (snippet.objectiveText) {
            document.execCommand('insertHTML', false, snippet.objectiveText);
        }
        // Append impression text to the Impressions & Recommendations section
        if (snippet.impressionText) {
            const impressionSection = sections.find(s => /impression|recommendation/i.test(s.title));
            if (impressionSection) {
                const el = editorRefs.current[impressionSection.id];
                if (el) {
                    const br = el.innerHTML.trim() ? '<br/>' : '';
                    el.innerHTML += br + snippet.impressionText;
                }
            }
        }
    }, [sections]);

    const removeSectionSnippet = useCallback(async (sectionTitle, snippetId) => {
        const current = sectionSnippets[sectionTitle] || [];
        const updated = current.filter(s => s.id !== snippetId);
        const updatedMap = { ...sectionSnippets, [sectionTitle]: updated };
        await saveSectionSnippets(updatedMap);
    }, [sectionSnippets, saveSectionSnippets]);

    const fileInputRefs = useRef({});
    const editorRef = useRef(null);
    const editorRefs = useRef({});
    const savedSelections = useRef({});
    const snippetRefs = useRef({});
    const recognitionRef = useRef(null);
    const [dictatingSection, setDictatingSection] = useState(null);

    const getCharOffset = useCallback((container) => {
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0) return null;
        const range = sel.getRangeAt(0);
        if (!container.contains(range.startContainer)) return null;
        const preRange = document.createRange();
        preRange.selectNodeContents(container);
        preRange.setEnd(range.startContainer, range.startOffset);
        return preRange.toString().length;
    }, []);

    const restoreCharOffset = useCallback((container, offset) => {
        const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null);
        let charCount = 0;
        let node;
        while ((node = walker.nextNode())) {
            const nextCount = charCount + node.textContent.length;
            if (nextCount >= offset) {
                const range = document.createRange();
                range.setStart(node, offset - charCount);
                range.collapse(true);
                const sel = window.getSelection();
                sel.removeAllRanges();
                sel.addRange(range);
                return true;
            }
            charCount = nextCount;
        }
        return false;
    }, []);

    // Insert dictated text at the caret inside a section's editor (or at the end
    // if the caret isn't currently in that editor).
    const insertDictatedText = useCallback((sectionId, text) => {
        const el = editorRefs.current[sectionId];
        if (!el) return;
        el.focus();
        const sel = window.getSelection();
        if (!sel.rangeCount || !el.contains(sel.anchorNode)) {
            const savedOffset = savedSelections.current[sectionId];
            if (typeof savedOffset === 'number' && restoreCharOffset(el, savedOffset)) {
                // caret restored to last known position
            } else {
                const range = document.createRange();
                range.selectNodeContents(el);
                range.collapse(false);
                sel.removeAllRanges();
                sel.addRange(range);
            }
        }
        // Add a leading space when the preceding character isn't already whitespace.
        const before = (el.innerText || '').replace(/ /g, ' ');
        const needsSpace = before.length > 0 && !/\s$/.test(before);
        document.execCommand('insertText', false, (needsSpace ? ' ' : '') + text + ' ');
        savedSelections.current[sectionId] = getCharOffset(el);
    }, [restoreCharOffset, getCharOffset]);

    // Toggle Web Speech API dictation for a given section's editor.
    const toggleDictation = useCallback((sectionId) => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognition) {
            alert("Voice dictation isn't supported in this browser. Please use Chrome or Edge.");
            return;
        }
        // Stop any in-progress dictation first.
        if (recognitionRef.current) {
            const wasActive = dictatingSection;
            try { recognitionRef.current.stop(); } catch (e) { /* noop */ }
            recognitionRef.current = null;
            setDictatingSection(null);
            // Tapping the same mic just stops; a different mic falls through to start.
            if (wasActive === sectionId) return;
        }
        const recognition = new SpeechRecognition();
        recognition.continuous = true;
        recognition.interimResults = false;
        recognition.lang = 'en-US';
        recognition.onresult = (event) => {
            let finalText = '';
            for (let i = event.resultIndex; i < event.results.length; i++) {
                if (event.results[i].isFinal) finalText += event.results[i][0].transcript;
            }
            finalText = finalText.trim();
            if (finalText) insertDictatedText(sectionId, finalText);
        };
        recognition.onerror = (event) => {
            if (event.error && event.error !== 'no-speech' && event.error !== 'aborted') {
                console.error('Voice dictation error:', event.error);
                if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
                    alert('Microphone access was blocked. Please allow microphone permission and try again.');
                }
            }
        };
        recognition.onend = () => {
            if (recognitionRef.current === recognition) {
                recognitionRef.current = null;
                setDictatingSection(null);
            }
        };
        recognitionRef.current = recognition;
        setDictatingSection(sectionId);
        const el = editorRefs.current[sectionId];
        if (el) el.focus();
        try { recognition.start(); } catch (e) { console.error('Failed to start dictation:', e); }
    }, [dictatingSection, insertDictatedText]);

    // Stop dictation if the component unmounts mid-session.
    useEffect(() => () => {
        if (recognitionRef.current) {
            try { recognitionRef.current.stop(); } catch (e) { /* noop */ }
            recognitionRef.current = null;
        }
    }, []);

    const snippetEditorRef = useRef(null);
    const snippetImpressionRef = useRef(null);
    const applyFormat = (command, value, target) => {
        const el = target || editorRef.current;
        const sel = window.getSelection();
        if (!sel.rangeCount) return;
        const range = sel.getRangeAt(0);
        const startText = range.startContainer.textContent;
        const startOffset = range.startOffset;
        const endText = range.endContainer.textContent;
        const endOffset = range.endOffset;
        const selectedText = sel.toString();
        document.execCommand(command, false, value || null);
        // Re-select: find the selected text within the editor after DOM mutation
        requestAnimationFrame(() => {
            const newSel = window.getSelection();
            if (newSel.rangeCount && newSel.toString().length > 0) return;
            // Try to find and re-select the text
            if (el && selectedText) {
                const treeWalker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
                let charCount = 0;
                let startNode = null, startOff = 0, endNode = null, endOff = 0;
                const fullText = el.innerText || el.textContent;
                const selIndex = fullText.indexOf(selectedText);
                if (selIndex === -1) return;
                while (treeWalker.nextNode()) {
                    const node = treeWalker.currentNode;
                    const nodeLen = node.textContent.length;
                    if (!startNode && charCount + nodeLen > selIndex) {
                        startNode = node;
                        startOff = selIndex - charCount;
                    }
                    if (!endNode && charCount + nodeLen >= selIndex + selectedText.length) {
                        endNode = node;
                        endOff = selIndex + selectedText.length - charCount;
                        break;
                    }
                    charCount += nodeLen;
                }
                if (startNode && endNode) {
                    const newRange = document.createRange();
                    newRange.setStart(startNode, startOff);
                    newRange.setEnd(endNode, endOff);
                    newSel.removeAllRanges();
                    newSel.addRange(newRange);
                }
            }
        });
    };

    const processImageFiles = useCallback((sectionId, files, subsection) => {
        const imageFiles = Array.from(files).filter(f => f.type.startsWith('image/'));
        if (!imageFiles.length) return;
        // Images only belong to Images-section subsections now; ignore stray
        // paste/drop into regular section bodies/editors.
        if (!subsection) return;

        const MAX_WIDTH = 670;
        const MAX_HEIGHT = 900;
        const MAX_FILE_SIZE = 10 * 1024 * 1024;

        imageFiles.forEach(file => {
            if (file.size > MAX_FILE_SIZE) {
                alert(`"${file.name}" exceeds the 10MB file size limit.`);
                return;
            }

            const reader = new FileReader();
            reader.onerror = () => {
                alert(`Failed to read file "${file.name}".`);
            };
            reader.onload = (e) => {
                const img = new Image();
                img.onerror = () => {
                    alert(`"${file.name}" could not be loaded as an image.`);
                };
                img.onload = () => {
                    const aspect = img.naturalHeight / img.naturalWidth;
                    let w = Math.min(img.naturalWidth, MAX_WIDTH);
                    let h = Math.round(w * aspect);
                    if (h > MAX_HEIGHT) {
                        h = MAX_HEIGHT;
                        w = Math.round(h / aspect);
                    }
                    const newImg = { id: Date.now() + Math.random(), src: e.target.result, name: file.name, width: w, height: h, naturalWidth: img.naturalWidth, naturalHeight: img.naturalHeight };
                    setSections(prev => prev.map(s => {
                        if (s.id !== sectionId) return s;
                        if (subsection && s.imageSubsections) {
                            return { ...s, imageSubsections: { ...s.imageSubsections, [subsection]: [...(s.imageSubsections[subsection] || []), newImg] } };
                        }
                        return { ...s, images: [...(s.images || []), newImg] };
                    }));
                };
                img.src = e.target.result;
            };
            reader.readAsDataURL(file);
        });
    }, []);

    const handleEditorPaste = useCallback((e, sectionId) => {
        const clipboard = e.clipboardData;
        if (!clipboard) return;
        const pastedFiles = clipboard.files;
        if (pastedFiles && pastedFiles.length > 0) {
            const imageFiles = Array.from(pastedFiles).filter(f => f.type.startsWith('image/'));
            if (imageFiles.length) {
                e.preventDefault();
                e.stopPropagation();
                if (sectionId != null) processImageFiles(sectionId, imageFiles);
                return;
            }
        }
        const items = clipboard.items;
        if (!items) return;
        const files = [];
        for (let i = 0; i < items.length; i++) {
            if (items[i].kind === 'file' && items[i].type.startsWith('image/')) {
                const file = items[i].getAsFile();
                if (file) files.push(file);
            }
        }
        if (files.length) {
            e.preventDefault();
            e.stopPropagation();
            if (sectionId != null) processImageFiles(sectionId, files);
        }
    }, [processImageFiles]);

    const handleEditorDrop = useCallback((e, sectionId) => {
        const files = e.dataTransfer?.files;
        if (!files) return;
        const imageFiles = [];
        for (let i = 0; i < files.length; i++) {
            if (files[i].type.startsWith('image/')) {
                imageFiles.push(files[i]);
            }
        }
        if (imageFiles.length) {
            e.preventDefault();
            e.stopPropagation();
            if (sectionId != null) {
                processImageFiles(sectionId, imageFiles);
            }
        }
    }, [processImageFiles]);

    const handleSectionDrop = useCallback((e, sectionId) => {
        e.preventDefault();
        e.stopPropagation();
        processImageFiles(sectionId, e.dataTransfer.files);
    }, [processImageFiles]);

    const handleSectionPaste = useCallback((e, sectionId) => {
        if (e.target.closest('[contenteditable="true"]')) return;
        const items = e.clipboardData?.items;
        if (!items) return;
        const files = [];
        for (let i = 0; i < items.length; i++) {
            if (items[i].type.startsWith('image/')) {
                files.push(items[i].getAsFile());
            }
        }
        if (files.length) {
            e.preventDefault();
            processImageFiles(sectionId, files);
        }
    }, [processImageFiles]);

    const MIN_ZOOM = 0.25;
    const MAX_ZOOM = 8;

    const openLightbox = useCallback((img) => {
        setLightbox(img);
        setLightboxZoom(1);
        setLightboxPan({ x: 0, y: 0 });
    }, []);

    const closeLightbox = useCallback(() => {
        setLightbox(null);
        setLightboxZoom(1);
        setLightboxPan({ x: 0, y: 0 });
    }, []);

    // Zoom toward a focal point (cx, cy) given in stage-local pixels. When no
    // point is given (button/keyboard), zoom toward the stage center.
    const zoomAt = useCallback((factor, cx, cy) => {
        setLightboxZoom(prevZoom => {
            const next = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, +(prevZoom * factor).toFixed(3)));
            const stage = lightboxStageRef.current;
            const fx = cx == null ? (stage ? stage.clientWidth / 2 : 0) : cx;
            const fy = cy == null ? (stage ? stage.clientHeight / 2 : 0) : cy;
            setLightboxPan(prevPan => ({
                x: fx - (fx - prevPan.x) * (next / prevZoom),
                y: fy - (fy - prevPan.y) * (next / prevZoom),
            }));
            return next;
        });
    }, []);

    const handleLightboxPanStart = useCallback((e) => {
        e.preventDefault();
        lightboxDragRef.current = { startX: e.clientX, startY: e.clientY };
        setLightboxPan(prev => {
            lightboxDragRef.current.panX = prev.x;
            lightboxDragRef.current.panY = prev.y;
            return prev;
        });
        const onMove = (moveE) => {
            const d = lightboxDragRef.current;
            if (!d) return;
            setLightboxPan({ x: d.panX + (moveE.clientX - d.startX), y: d.panY + (moveE.clientY - d.startY) });
        };
        const onUp = () => {
            lightboxDragRef.current = null;
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
        };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    }, []);

    useEffect(() => {
        if (!lightbox) return;
        const onKey = (e) => {
            if (e.key === 'Escape') closeLightbox();
            else if (e.key === '+' || e.key === '=') zoomAt(1.15);
            else if (e.key === '-' || e.key === '_') zoomAt(1 / 1.15);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [lightbox, closeLightbox, zoomAt]);

    const removeImage = useCallback((sectionId, imageId, subsection) => {
        setSections(prev => prev.map(s => {
            if (s.id !== sectionId) return s;
            if (subsection && s.imageSubsections) {
                return { ...s, imageSubsections: { ...s.imageSubsections, [subsection]: s.imageSubsections[subsection].filter(img => img.id !== imageId) } };
            }
            return { ...s, images: s.images.filter(img => img.id !== imageId) };
        }));
    }, []);

    const handleResizeStart = useCallback((e, sectionId, imageId, startWidth, startHeight, subsection) => {
        e.preventDefault();
        const startX = e.clientX;
        const aspect = startHeight / startWidth;

        const onMouseMove = (moveE) => {
            const delta = moveE.clientX - startX;
            const newWidth = Math.max(60, Math.min(startWidth + delta, 670));
            const newHeight = Math.round(newWidth * aspect);
            setSections(prev => prev.map(s => {
                if (s.id !== sectionId) return s;
                if (subsection && s.imageSubsections) {
                    return { ...s, imageSubsections: { ...s.imageSubsections, [subsection]: s.imageSubsections[subsection].map(img => img.id === imageId ? { ...img, width: newWidth, height: newHeight } : img) } };
                }
                return { ...s, images: s.images.map(img => img.id === imageId ? { ...img, width: newWidth, height: newHeight } : img) };
            }));
        };

        const onMouseUp = () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    }, []);

    const handleSectionDragStart = useCallback((e, index) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', index.toString());
    }, []);

    const handleSectionDragOver = useCallback((e, index) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOverIndex(index);
    }, []);

    const handleSectionDragEnd = useCallback(() => {
        if (draggedIndex !== null && dragOverIndex !== null && draggedIndex !== dragOverIndex) {
            setSections(prev => {
                const updated = [...prev];
                const [moved] = updated.splice(draggedIndex, 1);
                updated.splice(dragOverIndex, 0, moved);
                return updated;
            });
        }
        setDraggedIndex(null);
        setDragOverIndex(null);
    }, [draggedIndex, dragOverIndex]);

    const addNewSection = useCallback(() => {
        const newId = Date.now();
        setSections(prev => [...prev, {
            id: newId,
            title: "New Section",
            content: [],
            images: [],
            enabled: true,
        }]);
        setEditingTitle(newId);
        setEditTitleText("New Section");
        setEditingSection(newId);
        setEditText("");
    }, []);

    const addToImageBank = useCallback((files) => {
        const imageFiles = Array.from(files).filter(f => f.type.startsWith('image/'));
        if (!imageFiles.length) return;
        const MAX_FILE_SIZE = 10 * 1024 * 1024;
        imageFiles.forEach(file => {
            if (file.size > MAX_FILE_SIZE) {
                alert(`"${file.name}" exceeds the 10MB file size limit.`);
                return;
            }
            const reader = new FileReader();
            reader.onerror = () => alert(`Failed to read file "${file.name}".`);
            reader.onload = (e) => {
                const img = new Image();
                img.onerror = () => alert(`"${file.name}" could not be loaded as an image.`);
                img.onload = () => {
                    const MAX_WIDTH = 670;
                    const MAX_HEIGHT = 900;
                    const aspect = img.naturalHeight / img.naturalWidth;
                    let w = Math.min(img.naturalWidth, MAX_WIDTH);
                    let h = Math.round(w * aspect);
                    if (h > MAX_HEIGHT) { h = MAX_HEIGHT; w = Math.round(h / aspect); }
                    setImageBank(prev => [...prev, {
                        id: Date.now() + Math.random(),
                        src: e.target.result,
                        name: file.name,
                        width: w,
                        height: h,
                        naturalWidth: img.naturalWidth,
                        naturalHeight: img.naturalHeight,
                    }]);
                };
                img.src = e.target.result;
            };
            reader.readAsDataURL(file);
        });
    }, []);

    const removeFromImageBank = useCallback((imageId) => {
        setImageBank(prev => prev.filter(img => img.id !== imageId));
    }, []);

    const insertBankImageToSection = useCallback((bankImage) => {
        const activeSection = lastFocusedSectionRef.current;
        if (!activeSection) {
            alert('Click on a section first, then click an image to insert it.');
            return;
        }
        setSections(prev => prev.map(s =>
            s.id === activeSection
                ? { ...s, images: [...(s.images || []), { ...bankImage, id: Date.now() + Math.random() }] }
                : s
        ));
    }, []);

    const handleImageBankDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        addToImageBank(e.dataTransfer.files);
    }, [addToImageBank]);

    const handleImageBankPaste = useCallback((e) => {
        const items = e.clipboardData?.items;
        if (!items) return;
        const files = [];
        for (let i = 0; i < items.length; i++) {
            if (items[i].type.startsWith('image/')) {
                files.push(items[i].getAsFile());
            }
        }
        if (files.length) {
            e.preventDefault();
            addToImageBank(files);
            // Clear the paste area content
            setTimeout(() => {
                if (e.target && e.target.contentEditable) {
                    e.target.innerHTML = '';
                }
            }, 0);
        }
    }, [addToImageBank]);

    const startEditingTitle = useCallback((section) => {
        setEditingTitle(section.id);
        setEditTitleText(section.title);
    }, []);

    const saveTitleEdit = useCallback((id) => {
        try {
            const trimmed = editTitleText.trim();
            if (!trimmed) {
                alert("Section title cannot be empty.");
                return;
            }
            if (trimmed.length > 100) {
                alert("Section title must be under 100 characters.");
                return;
            }
            setSections(prev => prev.map(s =>
                s.id === id ? { ...s, title: trimmed } : s
            ));
            setEditingTitle(null);
            setEditTitleText("");
        } catch (err) {
            alert("Failed to save section title. Please try again.");
        }
    }, [editTitleText]);

    const removeSection = useCallback((id) => {
        if (!window.confirm("Are you sure you want to remove this section?")) return;
        setSections(prev => prev.filter(s => s.id !== id));
    }, []);

    const addImageSubsection = useCallback((sectionId) => {
        setSections(prev => prev.map(s => {
            if (s.id !== sectionId) return s;
            const existing = s.imageSubsections || {};
            let name = "New Subsection";
            let i = 2;
            while (Object.prototype.hasOwnProperty.call(existing, name)) {
                name = `New Subsection ${i++}`;
            }
            return { ...s, imageSubsections: { ...existing, [name]: [] } };
        }));
    }, []);

    const removeImageSubsection = useCallback((sectionId, name) => {
        if (!window.confirm(`Remove the "${name}" subsection and its images?`)) return;
        setSections(prev => prev.map(s => {
            if (s.id !== sectionId || !s.imageSubsections) return s;
            const { [name]: _removed, ...rest } = s.imageSubsections;
            return { ...s, imageSubsections: rest };
        }));
    }, []);

    const startEditingSubsection = useCallback((sectionId, name) => {
        setEditingSubsection({ sectionId, name });
        setEditSubsectionText(name);
    }, []);

    const saveSubsectionTitle = useCallback((sectionId, oldName) => {
        const trimmed = editSubsectionText.trim();
        setEditingSubsection(null);
        setEditSubsectionText("");
        if (!trimmed || trimmed === oldName) return;
        setSections(prev => prev.map(s => {
            if (s.id !== sectionId || !s.imageSubsections) return s;
            if (Object.prototype.hasOwnProperty.call(s.imageSubsections, trimmed)) {
                alert("A subsection with that name already exists.");
                return s;
            }
            // Rebuild to preserve key order with the renamed key in place.
            const rebuilt = {};
            Object.keys(s.imageSubsections).forEach(k => {
                rebuilt[k === oldName ? trimmed : k] = s.imageSubsections[k];
            });
            return { ...s, imageSubsections: rebuilt };
        }));
    }, [editSubsectionText]);

    const createNewTemplate = async () => {
        try {
            const name = prompt("Enter a name for your new template:");
            if (!name || !name.trim()) return;
            if (name.trim().length > 50) {
                alert("Template name must be under 50 characters.");
                return;
            }
            if (customTemplates.some(t => t.name.toLowerCase() === name.trim().toLowerCase())) {
                alert("A template with that name already exists.");
                return;
            }
            const templateId = 'custom_' + Date.now();
            const baseSections = generalTemplateSaved && generalTemplateSaved.sections
                ? generalTemplateSaved.sections.map((s, i) => ({
                    id: Date.now() + i,
                    title: s.title,
                    content: s.content || [""],
                    images: [],
                    enabled: s.enabled !== undefined ? s.enabled : true,
                }))
                : sections.map((s, i) => ({
                    id: Date.now() + i,
                    title: s.title,
                    content: s.content || [""],
                    images: [],
                    enabled: s.enabled,
                }));
            // Ensure Images section exists with subsections
            const hasImages = baseSections.some(s => /^images$/i.test(s.title.trim()));
            if (!hasImages) {
                baseSections.push({
                    id: Date.now() + 999,
                    title: "Images",
                    content: [],
                    images: [],
                    enabled: true,
                    imageSubsections: {
                        Panoramic: [],
                        Airway: [],
                        TMJ: [],
                    },
                });
            }
            const newTemplate = {
                id: templateId,
                name: name.trim(),
                sections: baseSections,
            };
            const updated = [...customTemplates, newTemplate];
            setCustomTemplates(updated);
            setActiveTemplate(templateId);
            setSections(newTemplate.sections);
            if (userAuthStatus) {
                try { await axios.post(SETTINGS_URL, { body: {
                    operation: 'saveTemplate',
                    userId: userAuthStatus,
                    template: { id: templateId, name: newTemplate.name, sections: newTemplate.sections.map(serializeSection) },
                } }); } catch (err) { console.error("Failed to save template:", err); }
            }
        } catch (err) {
            alert("Failed to create template. Please try again.");
        }
    };

    const renameCustomTemplate = async (templateId) => {
        try {
            const tpl = customTemplates.find(t => t.id === templateId);
            if (!tpl) return;
            const newName = prompt("Rename template:", tpl.name);
            if (!newName || !newName.trim() || newName.trim() === tpl.name) return;
            if (newName.trim().length > 50) {
                alert("Template name must be under 50 characters.");
                return;
            }
            if (customTemplates.some(t => t.id !== templateId && t.name.toLowerCase() === newName.trim().toLowerCase())) {
                alert("A template with that name already exists.");
                return;
            }
            setCustomTemplates(prev => prev.map(t => t.id === templateId ? { ...t, name: newName.trim() } : t));
            if (userAuthStatus) {
                try { await axios.post(SETTINGS_URL, { body: {
                    operation: 'saveTemplate',
                    userId: userAuthStatus,
                    template: { id: tpl.id, name: newName.trim(), sections: tpl.sections.map(serializeSection) },
                } }); } catch (err) { console.error("Failed to rename template:", err); }
            }
        } catch (err) {
            alert("Failed to rename template. Please try again.");
        }
    };

    const renameGeneralTemplate = async () => {
        try {
            const newName = prompt("Rename General Template:", generalTemplateName);
            if (!newName || !newName.trim() || newName.trim() === generalTemplateName) return;
            const trimmed = newName.trim();
            if (trimmed.length > 50) {
                alert("Template name must be under 50 characters.");
                return;
            }
            if (customTemplates.some(t => t.name.toLowerCase() === trimmed.toLowerCase())) {
                alert("A template with that name already exists.");
                return;
            }
            setGeneralTemplateName(trimmed);
            setGeneralTemplateSaved(prev => ({ ...(prev || {}), name: trimmed }));
            if (userAuthStatus) {
                try {
                    const savedSections = (generalTemplateSaved && generalTemplateSaved.sections)
                        ? generalTemplateSaved.sections
                        : sections.map(syncSection).map(serializeSection);
                    await axios.post(SETTINGS_URL, { body: {
                        operation: 'saveGeneralTemplate',
                        userId: userAuthStatus,
                        template: { name: trimmed, sections: savedSections },
                    } });
                } catch (err) {
                    console.error("Failed to rename general template:", err);
                }
            }
        } catch (err) {
            alert("Failed to rename template. Please try again.");
        }
    };

    const deleteCustomTemplate = async (templateId) => {
        if (!window.confirm("Are you sure you want to delete this template?")) return;
        const updated = customTemplates.filter(t => t.id !== templateId);
        setCustomTemplates(updated);
        if (activeTemplate === templateId) {
            switchTemplate('general');
        }
        if (userAuthStatus) {
            try {
                await axios.post(SETTINGS_URL, { body: { operation: 'deleteTemplate', userId: userAuthStatus, templateId } });
            } catch (err) {
                console.error("Failed to delete template from server:", err);
            }
        }
    };

    useEffect(() => {
        if (!activeTemplate.startsWith('custom_')) return;

        // Update customTemplates in memory with current sections
        const current = customTemplatesRef.current;
        if (!current.find(t => t.id === activeTemplate)) return;
        const updated = current.map(t =>
            t.id === activeTemplate ? { ...t, sections: sections.map(s => ({ ...s, images: s.images || [] })) } : t
        );
        setCustomTemplates(updated);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sections, activeTemplate]);

    const onChangeColorMode = () => {
        const next = !colorMode;
        setColorMode(next);
        localStorage.setItem('colorMode', next);
    };

    const switchTemplate = useCallback((template) => {
        setActiveTemplate(template);
        setEditingSection(null);
        setEditText("");

        if (template.startsWith('custom_')) {
            const found = customTemplates.find(t => t.id === template);
            if (found) setSections(found.sections);
            return;
        }

        setSections(prev => {
            const imageMap = {};
            prev.forEach(s => { imageMap[s.id] = s.images || []; });

            let filterFn;
            if (template === 'general') {
                filterFn = () => true;
            } else {
                const customTpl = customTemplates.find(t => t.id === template);
                if (customTpl) {
                    filterFn = (s) => customTpl.sections.includes(s.id);
                } else {
                    filterFn = () => true;
                }
            }

            return allSections.map(s => ({
                ...s,
                images: imageMap[s.id] || [],
                enabled: filterFn(s),
            }));
        });
    }, [allSections, customTemplates]);

    // Editors emit colors as either hex (#RRGGBB) or rgb()/rgba() (from
    // execCommand('foreColor')). Returns [r,g,b] or null.
    const parseColor = (col) => {
        if (!col) return null;
        const s = String(col).trim();
        const rgbMatch = s.match(/rgba?\(([^)]+)\)/i);
        if (rgbMatch) {
            const parts = rgbMatch[1].split(',').slice(0, 3).map(p => parseInt(p.trim(), 10));
            return (parts.length === 3 && parts.every(n => !isNaN(n))) ? parts : null;
        }
        let hex = s.replace('#', '');
        if (hex.length === 3) hex = hex.split('').map(c => c + c).join('');
        if (!/^[0-9a-fA-F]{6}$/.test(hex)) return null;
        return [parseInt(hex.substring(0, 2), 16), parseInt(hex.substring(2, 4), 16), parseInt(hex.substring(4, 6), 16)];
    };

    const parseHtmlSegments = (html) => {
        const div = document.createElement('div');
        div.innerHTML = DOMPurify.sanitize(html);
        const segments = [];

        const walk = (node, style) => {
            if (node.nodeType === 3) {
                const text = node.textContent;
                if (text) segments.push({ text, ...style });
                return;
            }
            if (node.nodeType !== 1) return;
            const tag = node.tagName.toLowerCase();
            const newStyle = { ...style };
            if (tag === 'b' || tag === 'strong') newStyle.bold = true;
            if (tag === 'i' || tag === 'em') newStyle.italic = true;
            if (tag === 'u') newStyle.underline = true;
            // Color can arrive as a <font color> attribute or an inline
            // style.color on any element (span, font, b, div, ...).
            if (node.style && node.style.color) newStyle.color = node.style.color;
            if (tag === 'font' && node.getAttribute('color')) newStyle.color = node.getAttribute('color');
            node.childNodes.forEach(child => walk(child, newStyle));
        };

        walk(div, { bold: false, italic: false, underline: false, color: null });
        return segments;
    };

    const buildPdf = useCallback(() => {
            // Read the latest text straight from the editors (contentEditable keeps
            // edits in the DOM until synced) so preview/export/save reflect changes.
            const enabledSections = sections.map(syncSection).filter(s => s.enabled);
            if (!enabledSections.length) {
                alert("No sections are enabled. Enable at least one section before exporting.");
                return null;
            }

            const pdf = new jsPDF({ unit: "pt", format: "letter" });
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const marginLeft = 54;
            const marginRight = 54;
            const contentWidth = pageWidth - marginLeft - marginRight;
            let y = 40;

            const TEAL = [31, 78, 88];
            const NAVY = [26, 41, 69];
            const BLACK = [0, 0, 0];

            const checkPageBreak = (needed) => {
                if (y + needed > pageHeight - 60) {
                    pdf.setFontSize(8);
                    pdf.setTextColor(...TEAL);
                    pdf.text("Carolina OMF Imaging", pageWidth / 2, pageHeight - 30, { align: "center" });
                    pdf.addPage();
                    y = 50;
                }
            };

            if (headerLogoBase64) {
                try {
                    pdf.addImage(headerLogoBase64, "PNG", marginLeft, y, 150, 28);
                } catch (e) { }
            }

            // Doctor names - right aligned
            const GRAY_TEXT = [140, 150, 160];
            pdf.setFontSize(9);
            pdf.setTextColor(...GRAY_TEXT);
            pdf.setFont("helvetica", "normal");
            pdf.text("W. Bruce Howerton, Jr. DDS, MS", pageWidth - marginRight, y + 8, { align: "right" });
            pdf.text("Nicholas Colvin DDS Oral and", pageWidth - marginRight, y + 19, { align: "right" });
            pdf.text("Maxillofacial Radiologists", pageWidth - marginRight, y + 30, { align: "right" });

            y += 45;

            // Two-tone bar: gray left + teal right with website
            const barHeight = 14;
            const barMid = pageWidth * 0.48;

            // Gray bar (left portion)
            pdf.setFillColor(160, 170, 185);
            pdf.rect(0, y, barMid + 20, barHeight, 'F');

            // Teal bar (right portion, overlapping slightly for the angled joint)
            pdf.setFillColor(...TEAL);
            pdf.rect(barMid, y, pageWidth - barMid, barHeight, 'F');

            // Angled transition (triangle)
            pdf.setFillColor(160, 170, 185);
            pdf.triangle(barMid - 10, y, barMid + 20, y, barMid - 10, y + barHeight, 'F');

            // Website text on teal bar
            pdf.setFontSize(9);
            pdf.setTextColor(255, 255, 255);
            pdf.setFont("helvetica", "normal");
            const wwwX = barMid + 30;
            pdf.text("www.", wwwX, y + 10);
            const wwwWidth = pdf.getTextWidth("www.");
            pdf.setFont("helvetica", "bold");
            pdf.text("carolinaomfimaging", wwwX + wwwWidth, y + 10);
            const boldWidth = pdf.getTextWidth("carolinaomfimaging");
            pdf.setFont("helvetica", "normal");
            pdf.text(".com", wwwX + wwwWidth + boldWidth, y + 10);

            y += barHeight + 12;
            y += 20;

            pdf.setFontSize(14);
            pdf.setTextColor(...TEAL);
            pdf.setFont("helvetica", "bold");
            pdf.text("OMF RADIOLOGY REPORT", pageWidth / 2, y, { align: "center" });
            y += 25;

            pdf.setFontSize(10);
            pdf.setTextColor(...BLACK);
            const colLeft = marginLeft;
            const labelWidth = 80;
            const emailValue = patient.contact_email || "";
            pdf.setFont("helvetica", "normal");
            pdf.setFontSize(10);
            const emailTextWidth = pdf.getTextWidth(emailValue);
            const minColRight = colLeft + labelWidth + emailTextWidth + 20;
            const colRight = Math.max(pageWidth / 2 + 10, minColRight);
            const rowHeight = 18;

            const infoGrid = [
                [{ label: "Doctor:", value: patient.practitioner_name || "" },     { label: "Patient:", value: patient.patient_name || "" }],
                [{ label: "Email:", value: patient.contact_email || "" },          { label: "DOB:", value: patient.patient_dob || "" }],
                [{ label: "Scan Date:", value: patient.patient_scan_date || "" },  { label: "Report Date:", value: patient.dttm ? new Date(patient.dttm).toISOString().slice(0, 10) : "" }],
            ];

            infoGrid.forEach(row => {
                checkPageBreak(rowHeight);
                row.forEach((cell, colIdx) => {
                    const x = colIdx === 0 ? colLeft : colRight;
                    pdf.setFont("helvetica", "bold");
                    pdf.text(cell.label, x, y);
                    pdf.setFont("helvetica", "normal");
                    pdf.text(cell.value, x + labelWidth, y);
                });
                y += rowHeight;
            });
            y += 10;

            // Page 1: Region, Introduction, Impressions & Recommendations
            // Page 2+: Objective Findings and subsections, Images
            const isPage1Section = (title) => {
                const t = title.toUpperCase();
                return t.includes("REGION") || t.includes("INTRODUCTION") || t.includes("IMPRESSION") || t.includes("RECOMMENDATION");
            };
            const isImagesSection_ = (title) => /^images$/i.test(title.trim());
            const page1Sections = enabledSections.filter(s => isPage1Section(s.title));
            const middleSections = enabledSections.filter(s => !isPage1Section(s.title) && !isImagesSection_(s.title));
            const imagesSections = enabledSections.filter(s => isImagesSection_(s.title));
            const orderedSections = [...page1Sections, ...middleSections, ...imagesSections];

            let insideObjective = false;
            let startedPage2 = false;

            orderedSections.forEach(section => {
                // Force page break before page 2 sections and add "Objective Findings" header
                if (!isPage1Section(section.title) && !isImagesSection_(section.title) && !startedPage2) {
                    startedPage2 = true;
                    pdf.setFontSize(8);
                    pdf.setTextColor(...TEAL);
                    pdf.text("Carolina OMF Imaging", pageWidth / 2, pageHeight - 30, { align: "center" });
                    pdf.addPage();
                    y = 50;
                    // Add Objective Findings header
                    insideObjective = true;
                    y += 8;
                    pdf.setFontSize(12);
                    pdf.setTextColor(...TEAL);
                    pdf.setFont("helvetica", "bold");
                    pdf.text("OBJECTIVE FINDINGS:", marginLeft, y);
                    y += 6;
                    pdf.setDrawColor(...TEAL);
                    pdf.setLineWidth(0.5);
                    pdf.line(marginLeft, y, pageWidth - marginRight, y);
                    y += 14;
                }

                // Force page break before IMAGES section
                const isImageSec = isImagesSection_(section.title);
                if (isImageSec) {
                    pdf.setFontSize(8);
                    pdf.setTextColor(...TEAL);
                    pdf.text("Carolina OMF Imaging", pageWidth / 2, pageHeight - 30, { align: "center" });
                    pdf.addPage();
                    y = 50;
                }

                checkPageBreak(40);

                const isMainSection = section.title.toUpperCase().includes("REGION") ||
                                      section.title.toUpperCase().includes("IMPRESSION") ||
                                      isImageSec;

                if (isMainSection && !isImageSec && insideObjective) {
                    insideObjective = false;
                }

                if (isMainSection) {
                    y += 8;
                    pdf.setFontSize(12);
                    pdf.setTextColor(...TEAL);
                    const titleUpper = section.title.toUpperCase();
                    const recMatch = titleUpper.match(/^(.+?)(\bRECOMMENDATIONS?\b.*)$/i);
                    if (recMatch) {
                        pdf.setFont("helvetica", "bold");
                        pdf.text(recMatch[1], marginLeft, y);
                        const partWidth = pdf.getTextWidth(recMatch[1]);
                        pdf.setFont("helvetica", "bolditalic");
                        pdf.text(recMatch[2] + ":", marginLeft + partWidth, y);
                    } else {
                        pdf.setFont("helvetica", "bold");
                        pdf.text(titleUpper + ":", marginLeft, y);
                    }
                    y += 6;
                    pdf.setDrawColor(...TEAL);
                    pdf.setLineWidth(0.5);
                    pdf.line(marginLeft, y, pageWidth - marginRight, y);
                    y += 14;

                    // Images section — render each subsection with title and side-by-side images
                    if (isImageSec && section.imageSubsections) {
                        const headerMargin = 50;
                        const footerMargin = 60;
                        const maxImgH = pageHeight - headerMargin - footerMargin;
                        const imgGap = 10;

                        Object.keys(section.imageSubsections).forEach(sub => {
                            const subImages = section.imageSubsections[sub] || [];
                            if (subImages.length === 0) return;

                            checkPageBreak(30);
                            y += 6;
                            pdf.setFontSize(10);
                            pdf.setTextColor(...BLACK);
                            pdf.setFont("helvetica", "bold");
                            pdf.text(sub + ":", marginLeft, y);
                            y += 14;

                            let rowX = marginLeft;
                            let rowMaxH = 0;
                            subImages.forEach(img => {
                                try {
                                    let imgW = (img.width || 280) * 0.75;
                                    let imgH = (img.height || 200) * 0.75;
                                    if (imgW > contentWidth) { const scale = contentWidth / imgW; imgW = contentWidth; imgH = imgH * scale; }
                                    if (imgH > maxImgH) { const scale = maxImgH / imgH; imgH = maxImgH; imgW = imgW * scale; }
                                    if (rowX + imgW > marginLeft + contentWidth && rowX > marginLeft) {
                                        y += rowMaxH + imgGap;
                                        rowX = marginLeft;
                                        rowMaxH = 0;
                                    }
                                    checkPageBreak(imgH + imgGap);
                                    pdf.addImage(img.src, rowX, y, imgW, imgH);
                                    rowX += imgW + imgGap;
                                    rowMaxH = Math.max(rowMaxH, imgH);
                                } catch (e) { }
                            });
                            y += rowMaxH + imgGap;
                        });
                        return;
                    }
                } else {
                    const subIndent = 0;
                    y += 6;
                    pdf.setFontSize(10);
                    pdf.setTextColor(...BLACK);
                    pdf.setFont("helvetica", "bold");
                    pdf.text(section.title + ":", marginLeft + subIndent, y);
                    y += 14;
                }

                const subIndent = 0;

                pdf.setFont("helvetica", "normal");
                pdf.setFontSize(9.5);
                pdf.setTextColor(...BLACK);

                const isNumbered = /impression|recommendation/i.test(section.title);
                let lineNum = 0;

                const contentLeft = marginLeft + subIndent;
                const isBulleted = insideObjective;

                const editorEl = editorRefs.current[section.id];
                const liveContent = editorEl ? editorEl.innerHTML
                    .replace(/<div>/gi, '\n').replace(/<\/div>/gi, '')
                    .replace(/<br\s*\/?>/gi, '\n')
                    .split('\n')
                    .map(l => l.trim())
                    .filter(l => l) : section.content;

                liveContent.forEach(line => {
                    const segments = parseHtmlSegments(line);
                    // Render segments with formatting
                    let lineX = contentLeft;
                    if (isNumbered) {
                        lineNum++;
                        const numText = lineNum + ". ";
                        pdf.setFont("helvetica", "normal");
                        pdf.setTextColor(...BLACK);
                        pdf.text(numText, contentLeft, y);
                        lineX = contentLeft + pdf.getTextWidth(numText);
                    } else if (isBulleted) {
                        pdf.setFont("helvetica", "normal");
                        pdf.setTextColor(...BLACK);
                        pdf.text("\u2022", contentLeft, y);
                        lineX = contentLeft + pdf.getTextWidth("\u2022  ");
                    }
                    const numIndent = isNumbered ? pdf.getTextWidth("0. ") : (isBulleted ? pdf.getTextWidth("\u2022  ") : 0);
                    let lineSegments = [];
                    let currentLineWidth = 0;

                    const flushLine = () => {
                        lineSegments.forEach(seg => {
                            const style = (seg.bold ? "bold" : "") + (seg.italic ? "italic" : "");
                            pdf.setFont("helvetica", style || "normal");
                            const segColor = seg.color ? parseColor(seg.color) : null;
                            if (segColor) {
                                pdf.setTextColor(segColor[0], segColor[1], segColor[2]);
                            } else {
                                pdf.setTextColor(...BLACK);
                            }
                            pdf.text(seg.text, lineX, y);
                            if (seg.underline) {
                                const tw = pdf.getTextWidth(seg.text);
                                const uc = segColor || BLACK;
                                pdf.setDrawColor(uc[0], uc[1], uc[2]);
                                pdf.setLineWidth(0.5);
                                pdf.line(lineX, y + 1.5, lineX + tw, y + 1.5);
                            }
                            lineX += pdf.getTextWidth(seg.text);
                        });
                        y += 13;
                        lineX = contentLeft + numIndent;
                        lineSegments = [];
                        currentLineWidth = 0;
                    };

                    segments.forEach(seg => {
                        const style = (seg.bold ? "bold" : "") + (seg.italic ? "italic" : "");
                        pdf.setFont("helvetica", style || "normal");
                        pdf.setFontSize(9.5);
                        const words = seg.text.split(' ');
                        words.forEach((word, wi) => {
                            const wordText = wi < words.length - 1 ? word + ' ' : word;
                            const wordWidth = pdf.getTextWidth(wordText);
                            if (currentLineWidth + wordWidth > contentWidth && lineSegments.length > 0) {
                                checkPageBreak(13);
                                flushLine();
                            }
                            lineSegments.push({ ...seg, text: wordText });
                            currentLineWidth += wordWidth;
                        });
                    });
                    if (lineSegments.length > 0) {
                        checkPageBreak(13);
                        flushLine();
                    }
                    y += 4;
                });

                if (section.images && section.images.length > 0) {
                    const headerMargin = 50;
                    const footerMargin = 60;
                    const maxImgH = pageHeight - headerMargin - footerMargin;

                    section.images.forEach(img => {
                        try {
                            let imgW = (img.width || 280) * 0.75;
                            let imgH = (img.height || 200) * 0.75;

                            if (imgW > contentWidth) {
                                const scale = contentWidth / imgW;
                                imgW = contentWidth;
                                imgH = imgH * scale;
                            }

                            if (imgH > maxImgH) {
                                const scale = maxImgH / imgH;
                                imgH = maxImgH;
                                imgW = imgW * scale;
                            }

                            checkPageBreak(imgH + 10);
                            pdf.addImage(img.src, marginLeft, y, imgW, imgH);
                            y += imgH + 10;
                        } catch (e) { }
                    });
                }
            });


            y += 10;
            checkPageBreak(40);
            pdf.setDrawColor(...NAVY);
            pdf.setLineWidth(0.5);
            pdf.line(marginLeft, y, pageWidth - marginRight, y);
            y += 14;
            pdf.setFontSize(8);
            pdf.setTextColor(...NAVY);
            pdf.setFont("helvetica", "bolditalic");
            pdf.text("Key", marginLeft, y);
            pdf.setFont("helvetica", "italic");
            const keyText = " - Noted from the review of patient three-dimensional reconstructed images, this/these findings may require follow-up/clinical correlation by a dental practitioner/physician.";
            const keyLines = pdf.splitTextToSize(keyText, contentWidth - 15);
            pdf.text(keyLines, marginLeft + 15, y);
            y += keyLines.length * 11 + 10;

            checkPageBreak(120);
            pdf.setFont("helvetica", "normal");
            pdf.setFontSize(9.5);
            pdf.setTextColor(...BLACK);
            pdf.text("Please contact Carolina OMF Imaging for any questions regarding volume data findings.", marginLeft, y);
            y += 20;
            pdf.text("Sincerely,", marginLeft, y);
            y += 10;

            // Signature image
            if (signatureBase64) {
                try {
                    pdf.addImage(signatureBase64, 'PNG', marginLeft, y, 80, 30);
                    y += 32;
                } catch (e) { y += 10; }
            } else {
                y += 10;
            }

            // Signature line
            pdf.setDrawColor(...BLACK);
            pdf.setLineWidth(0.5);
            pdf.line(marginLeft, y, marginLeft + 90, y);
            y += 10;

            pdf.setFont("helvetica", "bold");
            pdf.setFontSize(9);
            pdf.setTextColor(...BLACK);
            pdf.text("Nicholas Colvin, DDS", marginLeft, y);
            y += 11;
            pdf.setFont("helvetica", "normal");
            pdf.text("Oral & Maxillofacial Radiologist", marginLeft, y);
            y += 20;

            // Contact info
            pdf.setFontSize(10);
            pdf.setTextColor(...TEAL);
            pdf.setFont("helvetica", "normal");
            pdf.text("\u260E", marginLeft, y);
            pdf.setTextColor(...BLACK);
            pdf.text("+1 (678) 995-2805", marginLeft + 10, y);
            y += 14;

            pdf.setTextColor(...TEAL);
            pdf.text("\u2295", marginLeft, y);
            pdf.setTextColor(...BLACK);
            pdf.text("www.carolinaomfimaging.com", marginLeft + 10, y);
            y += 12;
            pdf.setTextColor(...TEAL);
            pdf.text("\u2709", marginLeft, y);
            pdf.setTextColor(...BLACK);
            pdf.text("ncolvin@carolinaomfimaging.com", marginLeft + 10, y);

            pdf.setFontSize(8);
            pdf.setTextColor(...TEAL);
            pdf.text("Carolina OMF Imaging", pageWidth / 2, pageHeight - 30, { align: "center" });

            return pdf;
    }, [sections, headerLogoBase64, signatureBase64, patient]);

    const syncSection = (s) => {
        const el = editorRefs.current[s.id];
        if (!el) return s;
        const lines = el.innerHTML
            .replace(/<div>/gi, '\n').replace(/<\/div>/gi, '')
            .replace(/<br\s*\/?>/gi, '\n')
            .split('\n')
            .map(l => l.trim())
            .filter(l => l);
        return { ...s, content: lines };
    };

    const syncEditorsToState = useCallback(() => {
        setSections(prev => prev.map(syncSection));
    }, []);

    const handleExportPdf = useCallback(() => {
        try {
            const pdf = buildPdf();
            if (pdf) pdf.save(`${patient.id || "OMF"}_template.pdf`);
        } catch (err) {
            alert("Failed to export PDF. Please try again.");
        }
    }, [buildPdf, patient]);

    const handlePreviewPdf = useCallback(() => {
        try {
            const pdf = buildPdf();
            if (pdf) {
                const blob = pdf.output('blob');
                const url = URL.createObjectURL(blob);
                window.open(url, '_blank');
            }
        } catch (err) {
            alert("Failed to preview PDF. Please try again.");
        }
    }, [buildPdf]);

    const handleSaveReportToRecord = async () => {
        if (!patient.id) {
            alert("No patient record is loaded. Open this template from a patient record to save the report.");
            return;
        }
        try {
            setSavingReport(true);
            syncEditorsToState();
            const pdf = buildPdf();
            if (!pdf) {
                alert("Failed to build the report PDF. Please try again.");
                setSavingReport(false);
                return;
            }
            const blob = pdf.output('blob');

            let headers = { "Content-Type": "application/json" };
            try {
                const session = await fetchAuthSession();
                if (session.tokens?.idToken) {
                    headers.Authorization = session.tokens.idToken.toString();
                }
            } catch (e) { /* fall back to unauthenticated request */ }

            // Snapshot the editors once (fresh content + images) for all saves.
            const synced = sections.map(syncSection);

            const signed = await axios.post(
                API_URLS.DATA,
                {
                    body: {
                        fileID: patient.id,
                        fileName: 'finalDoc.pdf',
                        fileType: 'application/pdf',
                        fileOperation: "getSignedUrl",
                    }
                },
                { headers }
            );

            await axios.put(signed.data, blob, {
                headers: { "Content-Type": 'application/pdf' },
            });

            // Images live in S3 (base64 payloads exceed DynamoDB's 400KB item
            // limit). Full section data incl. images -> reportSections.json.
            // Non-fatal: if this fails, the report PDF is still saved.
            try {
                const reportData = { version: 1, activeTemplate, sections: synced };
                const jsonBlob = new Blob([JSON.stringify(reportData)], { type: 'application/json' });
                const signedJson = await axios.post(
                    API_URLS.DATA,
                    {
                        body: {
                            fileID: patient.id,
                            fileName: 'reportSections.json',
                            fileType: 'application/json',
                            fileOperation: "getSignedUrl",
                        }
                    },
                    { headers }
                );
                await axios.put(signedJson.data, jsonBlob, {
                    headers: { "Content-Type": 'application/json' },
                });
            } catch (jsonErr) {
                console.error("Report PDF saved, but section images (reportSections.json) failed to save:", jsonErr);
            }

            // Section CONTENT (no images) -> DynamoDB per record, so the report
            // can be reopened and re-edited anytime. Non-fatal.
            // Backend op: DATA "saveReport" (see contract in README/handoff).
            try {
                const contentSections = synced.map(s => ({ id: s.id, title: s.title, content: s.content, enabled: s.enabled }));
                await axios.post(
                    API_URLS.DATA,
                    {
                        body: {
                            fileID: patient.id,
                            fileOperation: "saveReport",
                            report: { version: 1, activeTemplate, sections: contentSections },
                        }
                    },
                    { headers }
                );
            } catch (ddbErr) {
                console.error("Report content failed to save to DynamoDB (saveReport op):", ddbErr);
            }

            alert("Report saved to the patient record.");
        } catch (err) {
            console.error("Failed to save report to record:", err);
            alert("Failed to save the report to the record. Please try again.");
        } finally {
            setSavingReport(false);
        }
    };

    const buildAuthHeaders = useCallback(async () => {
        const headers = { "Content-Type": "application/json" };
        try {
            const session = await fetchAuthSession();
            if (session.tokens?.idToken) {
                headers.Authorization = session.tokens.idToken.toString();
            }
        } catch (e) { /* fall back to unauthenticated request */ }
        return headers;
    }, []);

    // Images (and, for legacy reports, full content) from S3 reportSections.json.
    const fetchS3ReportSections = useCallback(async (headers) => {
        const response = await axios.post(
            API_URLS.DATA,
            {
                body: {
                    fileID: patient.id,
                    fileName: 'reportSections.json',
                    fileOperation: "download",
                }
            },
            { headers }
        );

        const extractUrl = (value) => {
            if (value == null) return null;
            if (typeof value === 'object') return extractUrl(value.body ?? value.url ?? null);
            if (typeof value === 'string') {
                const s = value.trim();
                if (/^https?:\/\//i.test(s)) return s;
                if (s.startsWith('"') || s.startsWith('{')) {
                    try { return extractUrl(JSON.parse(s)); } catch (_) { return null; }
                }
            }
            return null;
        };
        const url = extractUrl(response.data);
        if (!url) return null;

        const dataRes = await fetch(url);
        if (!dataRes.ok) return null;
        const report = await dataRes.json();
        const loaded = Array.isArray(report?.sections) ? report.sections : null;
        return (loaded && loaded.length) ? loaded : null;
    }, [patient.id]);

    // Section content from DynamoDB (authoritative for editing). Backend op:
    // DATA "getReport" -> returns the stored report object for the record.
    const fetchDdbReportSections = useCallback(async (headers) => {
        const res = await axios.post(
            API_URLS.DATA,
            { body: { fileID: patient.id, fileOperation: "getReport" } },
            { headers }
        );
        const parse = (v) => {
            if (v == null) return null;
            if (typeof v === 'string') { try { return JSON.parse(v); } catch (_) { return null; } }
            if (typeof v === 'object') return v;
            return null;
        };
        let report = parse(res.data);
        // Unwrap common API Gateway shapes: { body: <json string|obj>, report }.
        if (report && report.body != null && !Array.isArray(report.sections)) {
            report = parse(report.body) || report;
        }
        if (report && report.report) report = report.report;
        const loaded = Array.isArray(report?.sections) ? report.sections : null;
        return (loaded && loaded.length) ? loaded : null;
    }, [patient.id]);

    const fetchSavedReportSections = useCallback(async () => {
        if (!patient.id) return null;
        const headers = await buildAuthHeaders();

        let ddbSections = null;
        try { ddbSections = await fetchDdbReportSections(headers); } catch (e) { /* getReport may not be deployed yet */ }

        let s3Sections = null;
        try { s3Sections = await fetchS3ReportSections(headers); } catch (e) { /* no images stored */ }

        if (ddbSections) {
            const s3ById = {};
            (s3Sections || []).forEach(s => { s3ById[s.id] = s; });
            return ddbSections.map(s => {
                const match = s3ById[s.id];
                return {
                    ...s,
                    images: (match && match.images) || [],
                    ...((match && match.imageSubsections) ? { imageSubsections: match.imageSubsections } : {}),
                };
            });
        }
        // Fallback: reports saved before DynamoDB content existed (S3 only).
        return s3Sections;
    }, [patient.id, buildAuthHeaders, fetchDdbReportSections, fetchS3ReportSections]);

    const applyLoadedSections = useCallback((loaded) => {
        editorRefs.current = {};
        setReportLoaded(true);
        setActiveTemplate('general');
        setSections(loaded.map(s => ({ ...s, images: s.images || [] })));
        setSectionsNonce(n => n + 1);
    }, []);

    // Auto-load this record's saved report into the sections when opening the
    // customizer, the same way saved templates appear automatically.
    useEffect(() => {
        if (loading || !userAuthStatus || !patient.id || !patient?.finalDoc?.finalDoc) return;
        let cancelled = false;
        (async () => {
            try {
                const loaded = await fetchSavedReportSections();
                if (!cancelled && loaded) applyLoadedSections(loaded);
            } catch (e) { /* no saved report data — fall back to base template */ }
        })();
        return () => { cancelled = true; };
    }, [loading, userAuthStatus, patient.id, patient?.finalDoc?.finalDoc, fetchSavedReportSections, applyLoadedSections]);

    const handleLoadSavedReport = async () => {
        if (!patient.id || !patient?.finalDoc?.finalDoc) {
            alert("No saved report is available for this record.");
            return;
        }
        try {
            setLoadingSavedReport(true);
            const loaded = await fetchSavedReportSections();
            if (!loaded) {
                alert("This report was saved before section editing was supported, so it can't be reopened into the editor.");
                return;
            }
            applyLoadedSections(loaded);
        } catch (err) {
            console.error("Failed to load saved report:", err);
            alert("Failed to load the saved report. It may not have editable data saved.");
        } finally {
            setLoadingSavedReport(false);
        }
    };

    const handleSaveTemplate = async () => {
        try {
            // Read the latest content straight from the editors so keystrokes
            // made right before saving aren't lost to async setState.
            const synced = sections.map(syncSection);
            syncEditorsToState();
            const enabledSections = synced.filter(s => s.enabled);
            if (!enabledSections.length) {
                alert("No sections are enabled. Enable at least one section before saving.");
                return;
            }

            if (userAuthStatus && activeTemplate.startsWith('custom_')) {
                const tpl = customTemplates.find(t => t.id === activeTemplate);
                if (tpl) {
                    try {
                        await axios.post(SETTINGS_URL, { body: {
                            operation: 'saveTemplate',
                            userId: userAuthStatus,
                            template: {
                                id: tpl.id,
                                name: tpl.name,
                                sections: synced.map(serializeSection),
                            },
                        } });
                        alert("Template saved successfully!");
                    } catch (err) {
                        console.error("Failed to save template:", err);
                        alert("Template saved locally but failed to sync to server.");
                    }
                    navigate('/customize-template');
                    return;
                }
            }

            // Save general template to DynamoDB
            setGeneralTemplateSaved({ name: generalTemplateName, sections: synced.map(serializeSection) });

            // Also save to sessionStorage for PDF export use
            const templateData = enabledSections.map(s => ({
                title: s.title,
                content: s.content,
            }));
            sessionStorage.setItem('customTemplate', JSON.stringify(templateData));

            if (userAuthStatus) {
                try {
                    await axios.post(SETTINGS_URL, { body: {
                        operation: 'saveGeneralTemplate',
                        userId: userAuthStatus,
                        template: {
                            name: generalTemplateName,
                            sections: synced.map(serializeSection),
                        },
                    } });
                    alert("General template saved successfully!");
                } catch (err) {
                    console.error("Failed to save general template:", err);
                    alert("Template saved locally but failed to sync to server.");
                }
            } else {
                alert("General template saved successfully!");
            }
        } catch (err) {
            alert("Failed to save template. Please try again.");
        }
    };

    if (!userAuthStatus) return null;

    return (
        <div id="body" data-theme={colorMode}>
            <div className="page template-page">
                <div className="actionBar">
                    <div className="actionBarImage">
                        <img alt="Carolina OMF Imaging" style={{height: 'clamp(36px, 5vh, 60px)', width: 'auto', padding: '8px 0'}} src={logo} />
                    </div>
                    <div className="template-header-title">
                        <h2>Customize Template</h2>
                    </div>
                    <div className="accountContainer">
                        <button className="accountItems" onClick={() => navigate('/data')} title="Back to dashboard">
                            <BsArrowLeft className="accountIconBack" size={22} />
                        </button>
                        <button className="accountItems" onClick={onChangeColorMode} title={colorMode ? 'Switch to light mode' : 'Switch to dark mode'}>
                            {colorMode ?
                                <BsFillSunFill className="accountIconSun" size={22}/>
                                :
                                <BsFillMoonFill className="accountIconMoon" size={22}/>
                            }
                        </button>
                    </div>
                </div>

                {patient.patient_name && recordBannerVisible && (
                    <>
                        <div className="template-record-banner">
                            <span className="template-record-label">Record:</span>
                            <span className="template-record-name">{patient.patient_name}</span>
                            {patient.practitioner_name && <span className="template-record-detail">Dr. {patient.practitioner_name}</span>}
                            {patient.patient_scan_date && <span className="template-record-detail">{patient.patient_scan_date}</span>}
                            <span className="template-record-id">{patient.id}</span>
                            <button className="template-record-close" onClick={() => setRecordBannerVisible(false)} aria-label="Dismiss">
                                <BsXCircle size={16} />
                            </button>
                        </div>
                        <div className="template-record-spacer" />
                    </>
                )}

                <div className="template-tab-bar">
                    <button
                        className={`template-tab${activeTemplate === 'general' ? ' template-tab-active' : ''}`}
                        onClick={() => switchTemplate('general')}
                        onContextMenu={(e) => { e.preventDefault(); renameGeneralTemplate(); }}
                        title="The default full set of report sections (right-click to rename)"
                    >
                        {generalTemplateName}
                    </button>

                    {settingsLoading ? (
                        <span className="template-tab" style={{ opacity: 0.6, cursor: 'default', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                            <div className="template-spinner" style={{ width: '14px', height: '14px', borderWidth: '2px' }}></div>
                            Loading templates...
                        </span>
                    ) : (
                        <>
                            {customTemplates.map(t => (
                                <button
                                    key={t.id}
                                    className={`template-tab${activeTemplate === t.id ? ' template-tab-active' : ''}`}
                                    onClick={() => switchTemplate(t.id)}
                                    onDoubleClick={(e) => { e.stopPropagation(); renameCustomTemplate(t.id); }}
                                    title="Double-click to rename"
                                >
                                    {t.name}
                                    <span
                                        style={{ marginLeft: '6px', opacity: 0.6, display: 'inline-flex', alignItems: 'center' }}
                                        onClick={(e) => { e.stopPropagation(); e.preventDefault(); deleteCustomTemplate(t.id); }}
                                    >
                                        <BsXCircle size={12} />
                                    </span>
                                </button>
                            ))}
                            <button
                                className="template-tab"
                                onClick={createNewTemplate}
                                title="Create new template"
                            >
                                <BsPlusCircle size={14} style={{ marginRight: '4px' }} />
                                New
                            </button>
                        </>
                    )}
                </div>

                <div className="template-container">
                    {loading ? (
                        <div className="template-loading">
                            <div className="template-spinner"></div>
                            <p>Loading template sections...</p>
                        </div>
                    ) : loadError ? (
                        <div className="template-error">
                            <p>Unable to load template: {loadError}</p>
                            <button className="template-back-btn" onClick={() => navigate('/data')}>
                                Return to Dashboard
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className="template-info-bar">
                                <span>{sections.filter(s => s.enabled).length} of {sections.length} sections enabled</span>
                            </div>
                            <div className="template-sections">
                                {sections.map((section, index) => (
                                    <div
                                        key={`${section.id}-${sectionsNonce}`}
                                        className={`template-section${section.enabled ? '' : ' template-section-disabled'}${draggedIndex === index ? ' template-section-dragging' : ''}${dragOverIndex === index ? ' template-section-dragover' : ''}`}
                                        onDragOver={e => handleSectionDragOver(e, index)}
                                        onDragEnd={handleSectionDragEnd}
                                        onDragLeave={() => setDragOverIndex(null)}
                                    >
                                        {/* Clipboard tab on left edge */}
                                        {clipboardPopout !== section.id && (
                                            <button
                                                className="section-clipboard-tab"
                                                onClick={() => setClipboardPopout(section.id)}
                                                title="Open clipboard"
                                            >
                                                <BsClipboard size={12} />
                                            </button>
                                        )}
                                        {clipboardPopout === section.id && (
                                            <span className="section-connector-arrow" />
                                        )}
                                        <div className="template-section-header">
                                            <div
                                                className="template-drag-handle"
                                                title="Drag to reorder"
                                                draggable
                                                onDragStart={e => handleSectionDragStart(e, index)}
                                            >
                                                <BsGripVertical size={16} />
                                            </div>
                                            <button
                                                className="template-toggle-btn"
                                                onClick={() => toggleSection(section.id)}
                                                title={section.enabled ? 'Disable section' : 'Enable section'}
                                            >
                                                {section.enabled ? <BsEye size={16} /> : <BsEyeSlash size={16} />}
                                            </button>
                                            {editingTitle === section.id ? (
                                                <input
                                                    className="template-title-input"
                                                    value={editTitleText}
                                                    onChange={e => setEditTitleText(e.target.value)}
                                                    onBlur={() => saveTitleEdit(section.id)}
                                                    onKeyDown={e => { if (e.key === 'Enter') saveTitleEdit(section.id); if (e.key === 'Escape') { setEditingTitle(null); setEditTitleText(""); } }}
                                                    autoFocus
                                                />
                                            ) : (
                                                <h3
                                                    className="template-section-title"
                                                    onDoubleClick={() => startEditingTitle(section)}
                                                    title="Double-click to rename"
                                                >
                                                    {section.title}
                                                </h3>
                                            )}
                                            <button
                                                className="template-edit-btn template-remove-section-btn"
                                                onClick={() => removeSection(section.id)}
                                                title="Remove section"
                                            >
                                                <BsTrash size={13} />
                                            </button>
                                        </div>
                                        {section.enabled && (
                                            <div
                                                className="template-section-body"
                                                onDrop={e => handleSectionDrop(e, section.id)}
                                                onDragOver={e => { e.preventDefault(); e.stopPropagation(); }}
                                                onPaste={e => handleSectionPaste(e, section.id)}
                                                onClick={() => { lastFocusedSectionRef.current = section.id; }}
                                                tabIndex={0}
                                            >
                                                {/^images$/i.test(section.title.trim()) ? (
                                                    <div className="image-subsections">
                                                        {Object.keys(section.imageSubsections || {}).map(sub => {
                                                            const subImages = (section.imageSubsections && section.imageSubsections[sub]) || [];
                                                            const isEditingSub = editingSubsection && editingSubsection.sectionId === section.id && editingSubsection.name === sub;
                                                            return (
                                                                <div
                                                                    key={sub}
                                                                    className="image-subsection"
                                                                    tabIndex={0}
                                                                    onPaste={e => {
                                                                        const items = e.clipboardData?.items;
                                                                        if (!items) return;
                                                                        const imageFiles = [];
                                                                        for (let i = 0; i < items.length; i++) {
                                                                            if (items[i].type.startsWith('image/')) {
                                                                                const file = items[i].getAsFile();
                                                                                if (file) imageFiles.push(file);
                                                                            }
                                                                        }
                                                                        if (imageFiles.length) {
                                                                            e.preventDefault();
                                                                            e.stopPropagation();
                                                                            processImageFiles(section.id, imageFiles, sub);
                                                                        }
                                                                    }}
                                                                    onDrop={e => {
                                                                        e.preventDefault();
                                                                        e.stopPropagation();
                                                                        const files = [...e.dataTransfer.files].filter(f => f.type.startsWith('image/'));
                                                                        if (files.length) processImageFiles(section.id, files, sub);
                                                                    }}
                                                                    onDragOver={e => { e.preventDefault(); e.stopPropagation(); }}
                                                                >
                                                                    <div className="image-subsection-header">
                                                                        {isEditingSub ? (
                                                                            <input
                                                                                type="text"
                                                                                className="image-subsection-title-input"
                                                                                value={editSubsectionText}
                                                                                autoFocus
                                                                                maxLength={60}
                                                                                onChange={e => setEditSubsectionText(e.target.value)}
                                                                                onBlur={() => saveSubsectionTitle(section.id, sub)}
                                                                                onKeyDown={e => {
                                                                                    if (e.key === 'Enter') { e.preventDefault(); saveSubsectionTitle(section.id, sub); }
                                                                                    else if (e.key === 'Escape') { setEditingSubsection(null); setEditSubsectionText(""); }
                                                                                }}
                                                                            />
                                                                        ) : (
                                                                            <>
                                                                                <span
                                                                                    className="image-subsection-title"
                                                                                    onClick={() => startEditingSubsection(section.id, sub)}
                                                                                    title="Click to rename"
                                                                                >
                                                                                    {sub}
                                                                                </span>
                                                                                <button
                                                                                    type="button"
                                                                                    className="image-subsection-edit-btn"
                                                                                    onClick={() => startEditingSubsection(section.id, sub)}
                                                                                    title="Rename subsection"
                                                                                >
                                                                                    <BsPencil size={11} />
                                                                                </button>
                                                                                <button
                                                                                    type="button"
                                                                                    className="image-subsection-remove-btn"
                                                                                    onClick={() => removeImageSubsection(section.id, sub)}
                                                                                    title="Remove subsection"
                                                                                >
                                                                                    <BsTrash size={11} />
                                                                                </button>
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                    {subImages.length > 0 && (
                                                                        <div className="template-section-images">
                                                                            {subImages.map(img => (
                                                                                <div key={img.id} className="template-image-wrapper" style={{ width: img.width || 'auto' }}>
                                                                                    <img
                                                                                        src={img.src}
                                                                                        alt={img.name}
                                                                                        className="template-section-img"
                                                                                        style={{ width: img.width ? `${img.width}px` : 'auto', height: img.height ? `${img.height}px` : 'auto', cursor: 'zoom-in' }}
                                                                                        draggable={false}
                                                                                        onClick={() => openLightbox(img)}
                                                                                    />
                                                                                    <button
                                                                                        className="template-image-zoom"
                                                                                        onClick={() => openLightbox(img)}
                                                                                        title="Open & zoom"
                                                                                    >
                                                                                        <BsZoomIn size={12} />
                                                                                    </button>
                                                                                    <button
                                                                                        className="template-image-remove"
                                                                                        onClick={() => removeImage(section.id, img.id, sub)}
                                                                                        title="Remove image"
                                                                                    >
                                                                                        <BsTrash size={12} />
                                                                                    </button>
                                                                                    <div
                                                                                        className="template-image-resize-handle"
                                                                                        onMouseDown={e => handleResizeStart(e, section.id, img.id, img.width, img.height, sub)}
                                                                                        title="Drag to resize"
                                                                                    />
                                                                                    <span className="template-image-dimensions">{Math.round(img.width)} x {Math.round(img.height)}</span>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                    <div className="template-image-actions">
                                                                        <input
                                                                            type="file"
                                                                            accept="image/*"
                                                                            multiple
                                                                            style={{ display: 'none' }}
                                                                            ref={el => { fileInputRefs.current[`${section.id}_${sub}`] = el; }}
                                                                            onChange={e => { processImageFiles(section.id, e.target.files, sub); e.target.value = ''; }}
                                                                        />
                                                                        <button
                                                                            className="template-attach-btn"
                                                                            onClick={() => fileInputRefs.current[`${section.id}_${sub}`]?.click()}
                                                                        >
                                                                            <BsImage size={13} />
                                                                            <span>Attach Image</span>
                                                                        </button>
                                                                        <span className="template-drop-hint">or drag & drop / paste</span>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                        <button
                                                            type="button"
                                                            className="image-subsection-add-btn"
                                                            onClick={() => addImageSubsection(section.id)}
                                                        >
                                                            <BsPlusCircle size={14} />
                                                            <span>Add Subsection</span>
                                                        </button>
                                                    </div>
                                                ) : (
                                                <>
                                                <div className="template-edit-area">
                                                    <div className="template-format-toolbar" onMouseDown={e => e.preventDefault()}>
                                                        <button type="button" className="template-format-btn" onClick={() => { editorRef.current = editorRefs.current[section.id]; applyFormat('bold'); }} title="Bold">
                                                            <BsTypeBold size={14} />
                                                        </button>
                                                        <button type="button" className="template-format-btn" onClick={() => { editorRef.current = editorRefs.current[section.id]; applyFormat('italic'); }} title="Italic">
                                                            <BsTypeItalic size={14} />
                                                        </button>
                                                        <button type="button" className="template-format-btn" onClick={() => { editorRef.current = editorRefs.current[section.id]; applyFormat('underline'); }} title="Underline">
                                                            <BsTypeUnderline size={14} />
                                                        </button>
                                                        <span className="template-format-divider"></span>
                                                        <button type="button" className="template-format-btn template-color-btn" onClick={() => { editorRef.current = editorRefs.current[section.id]; applyFormat('foreColor', '#E67E22'); }} title="Orange">
                                                            <span className="template-color-dot" style={{background: '#E67E22'}}></span>
                                                        </button>
                                                        <button type="button" className="template-format-btn template-color-btn" onClick={() => { editorRef.current = editorRefs.current[section.id]; applyFormat('foreColor', '#2980B9'); }} title="Blue">
                                                            <span className="template-color-dot" style={{background: '#2980B9'}}></span>
                                                        </button>
                                                        <button type="button" className="template-format-btn template-color-btn" onClick={() => { editorRef.current = editorRefs.current[section.id]; applyFormat('foreColor', '#F1C40F'); }} title="Yellow">
                                                            <span className="template-color-dot" style={{background: '#F1C40F'}}></span>
                                                        </button>
                                                        <button type="button" className="template-format-btn template-color-btn" onClick={() => { editorRef.current = editorRefs.current[section.id]; applyFormat('foreColor', '#E74C3C'); }} title="Red">
                                                            <span className="template-color-dot" style={{background: '#E74C3C'}}></span>
                                                        </button>
                                                        <button type="button" className="template-format-btn template-color-btn" onClick={() => { editorRef.current = editorRefs.current[section.id]; applyFormat('foreColor', '#2E7D32'); }} title="Green">
                                                            <span className="template-color-dot" style={{background: '#2E7D32'}}></span>
                                                        </button>
                                                        <button type="button" className="template-format-btn template-color-btn" onClick={() => { editorRef.current = editorRefs.current[section.id]; applyFormat('foreColor', '#000000'); }} title="Black">
                                                            <span className="template-color-dot" style={{background: '#000000'}}></span>
                                                        </button>
                                                        <span className="template-format-divider"></span>
                                                        <button type="button" className="template-format-btn" onClick={() => { editorRef.current = editorRefs.current[section.id]; applyFormat('insertUnorderedList'); }} title="Bullet list">
                                                            <BsListUl size={14} />
                                                        </button>
                                                        <button type="button" className="template-format-btn" onClick={() => { editorRef.current = editorRefs.current[section.id]; applyFormat('insertOrderedList'); }} title="Numbered list">
                                                            <BsListOl size={14} />
                                                        </button>
                                                        <button type="button" className="template-format-btn" onClick={() => { editorRef.current = editorRefs.current[section.id]; applyFormat('outdent'); }} title="Decrease indent">
                                                            <BsTextIndentRight size={14} />
                                                        </button>
                                                        <button type="button" className="template-format-btn" onClick={() => { editorRef.current = editorRefs.current[section.id]; applyFormat('indent'); }} title="Increase indent">
                                                            <BsTextIndentLeft size={14} />
                                                        </button>
                                                        <span className="template-format-divider"></span>
                                                        <button type="button" className="template-format-btn" onClick={() => { editorRef.current = editorRefs.current[section.id]; applyFormat('undo'); }} title="Undo">
                                                            <BsArrowCounterclockwise size={12} />
                                                        </button>
                                                        <button type="button" className="template-format-btn" onClick={() => { editorRef.current = editorRefs.current[section.id]; applyFormat('redo'); }} title="Redo">
                                                            <BsArrowClockwise size={12} />
                                                        </button>
                                                        <span className="template-format-divider"></span>
                                                        <button
                                                            type="button"
                                                            className={`template-format-btn template-dictate-btn${dictatingSection === section.id ? ' dictating' : ''}`}
                                                            onClick={() => toggleDictation(section.id)}
                                                            title={dictatingSection === section.id ? 'Stop dictation' : 'Dictate with voice'}
                                                        >
                                                            {dictatingSection === section.id ? <BsMicFill size={14} /> : <BsMic size={14} />}
                                                        </button>
                                                    </div>
                                                    <div
                                                        ref={el => {
                                                            if (el && !editorRefs.current[section.id]) {
                                                                el.innerHTML = DOMPurify.sanitize(section.content.join('<br/>'));
                                                            }
                                                            editorRefs.current[section.id] = el;
                                                        }}
                                                        className="template-textarea template-richtext"
                                                        contentEditable
                                                        suppressContentEditableWarning
                                                        onKeyDown={e => {
                                                            if (e.key === 'Tab') {
                                                                e.preventDefault();
                                                                const sel = window.getSelection();
                                                                let node = sel && sel.anchorNode;
                                                                let inList = false;
                                                                while (node && node !== e.currentTarget) {
                                                                    if (node.nodeName === 'LI') { inList = true; break; }
                                                                    node = node.parentNode;
                                                                }
                                                                if (!inList) {
                                                                    document.execCommand('insertUnorderedList', false, null);
                                                                } else {
                                                                    document.execCommand(e.shiftKey ? 'outdent' : 'indent', false, null);
                                                                }
                                                            }
                                                        }}
                                                        onPaste={e => handleEditorPaste(e, section.id)}
                                                        onDrop={e => handleEditorDrop(e, section.id)}
                                                        onDragOver={e => { e.preventDefault(); e.stopPropagation(); }}
                                                        onBlur={() => {
                                                            const el = editorRefs.current[section.id];
                                                            const offset = el ? getCharOffset(el) : null;
                                                            if (offset !== null) {
                                                                savedSelections.current[section.id] = offset;
                                                            }
                                                        }}
                                                    />
                                                    <div className="section-snippets-bar" onMouseDown={e => e.preventDefault()}>
                                                        {(() => {
                                                            const barColors = ['#2980B9', '#27AE60', '#E67E22', '#8E44AD', '#E74C3C', '#16A085', '#D35400', '#2C3E50'];
                                                            const all = sectionSnippets[section.title] || [];
                                                            const groups = {};
                                                            all.forEach(s => {
                                                                const cat = (s.category || '').trim() || '';
                                                                if (!groups[cat]) groups[cat] = [];
                                                                groups[cat].push(s);
                                                            });
                                                            const cats = Object.keys(groups).sort((a, b) => !a ? 1 : !b ? -1 : a.localeCompare(b));
                                                            const hasCategories = cats.some(c => c !== '');
                                                            return cats.map((cat, ci) => {
                                                                const color = barColors[ci % barColors.length];
                                                                return (
                                                                <div key={cat || '__uncategorized'} className="snippet-bar-group" style={{ borderLeftColor: color }}>
                                                                    {hasCategories && cat && <span className="snippet-bar-group-label" style={{ color }}>{cat}</span>}
                                                                    {groups[cat].map(snippet => (
                                                                        <button
                                                                            key={snippet.id}
                                                                            className="section-snippet-btn"
                                                                            onClick={() => insertSnippetDual(snippet)}
                                                                            title={cat ? `${cat}: ${snippet.label}` : snippet.label}
                                                                            style={{ borderColor: color }}
                                                                        >
                                                                            {snippet.label}
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                                );
                                                            });
                                                        })()}
                                                    </div>
                                                    {clipboardPopout === section.id && (
                                                        <div className="section-clipboard-popout">
                                                            <div className="section-clipboard-header">
                                                                <span className="section-clipboard-title">{section.title}</span>
                                                                <button className="section-clipboard-close" onClick={() => setClipboardPopout(null)}>
                                                                    <BsXLg size={10} />
                                                                </button>
                                                            </div>
                                                            <div className="section-clipboard-body">
                                                                {(() => {
                                                                    const allSnippets = sectionSnippets[section.title] || [];
                                                                    const groups = {};
                                                                    allSnippets.forEach(s => {
                                                                        const cat = (s.category || '').trim() || 'Uncategorized';
                                                                        if (!groups[cat]) groups[cat] = [];
                                                                        groups[cat].push(s);
                                                                    });
                                                                    const categoryNames = Object.keys(groups).sort((a, b) => a === 'Uncategorized' ? 1 : b === 'Uncategorized' ? -1 : a.localeCompare(b));
                                                                    const hasMultipleCategories = categoryNames.length > 1 || (categoryNames.length === 1 && categoryNames[0] !== 'Uncategorized');

                                                                    const toggleCategory = (sectionId, cat) => {
                                                                        const key = `${sectionId}_${cat}`;
                                                                        setCollapsedCategories(prev => ({ ...prev, [key]: !prev[key] }));
                                                                    };

                                                                    const popoutColors = ['#2980B9', '#27AE60', '#E67E22', '#8E44AD', '#E74C3C', '#16A085', '#D35400', '#2C3E50'];

                                                                    return categoryNames.map((cat, ci) => {
                                                                        const collapseKey = `${section.id}_${cat}`;
                                                                        const isCollapsed = collapsedCategories[collapseKey];
                                                                        const groupColor = popoutColors[ci % popoutColors.length];
                                                                        return (
                                                                        <div key={cat} className="snippet-category-group" style={{ borderColor: groupColor }}>
                                                                            {hasMultipleCategories && (
                                                                                <div className="snippet-category-label snippet-category-toggle" onClick={() => toggleCategory(section.id, cat)} style={{ color: groupColor }}>
                                                                                    {isCollapsed ? <BsChevronRight size={8} /> : <BsChevronDown size={8} />}
                                                                                    <span>{cat}</span>
                                                                                </div>
                                                                            )}
                                                                            {!isCollapsed && groups[cat].map(snippet => {
                                                                    const isEditing = editingSnippetId === snippet.id;
                                                                    return (
                                                                    <div key={snippet.id} className={`section-clipboard-snippet${isEditing ? ' snippet-expanded' : ''}`}>
                                                                        <div className="section-clipboard-snippet-header" onClick={() => setEditingSnippetId(isEditing ? null : snippet.id)}>
                                                                            <span className="section-clipboard-snippet-label">{snippet.label}</span>
                                                                            <div className="section-clipboard-snippet-actions" onClick={e => e.stopPropagation()}>
                                                                                <button
                                                                                    className="section-snippet-insert"
                                                                                    onMouseDown={e => e.preventDefault()}
                                                                                    onClick={() => insertSnippetDual(snippet)}
                                                                                    title="Insert snippet"
                                                                                >
                                                                                    <BsPlusCircle size={10} />
                                                                                </button>
                                                                                <button
                                                                                    className="quick-text-snippet-remove"
                                                                                    onClick={() => removeSectionSnippet(section.title, snippet.id)}
                                                                                    title="Remove"
                                                                                >
                                                                                    <BsTrash size={9} />
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                        {isEditing && (
                                                                            <>
                                                                                <label className="snippet-field-label">Objective</label>
                                                                                <div className="template-format-toolbar snippet-popout-toolbar" onMouseDown={e => e.preventDefault()}>
                                                                                    <button type="button" className="template-format-btn" onClick={() => applyFormat('bold', null, snippetRefs.current[`${section.title}_${snippet.id}`])} title="Bold"><BsTypeBold size={11} /></button>
                                                                                    <button type="button" className="template-format-btn" onClick={() => applyFormat('italic', null, snippetRefs.current[`${section.title}_${snippet.id}`])} title="Italic"><BsTypeItalic size={11} /></button>
                                                                                    <button type="button" className="template-format-btn" onClick={() => applyFormat('underline', null, snippetRefs.current[`${section.title}_${snippet.id}`])} title="Underline"><BsTypeUnderline size={11} /></button>
                                                                                    <span className="template-format-divider"></span>
                                                                                    <button type="button" className="template-format-btn template-color-btn" onClick={() => applyFormat('foreColor', '#E67E22', snippetRefs.current[`${section.title}_${snippet.id}`])} title="Orange"><span className="template-color-dot" style={{background: '#E67E22'}}></span></button>
                                                                                    <button type="button" className="template-format-btn template-color-btn" onClick={() => applyFormat('foreColor', '#2980B9', snippetRefs.current[`${section.title}_${snippet.id}`])} title="Blue"><span className="template-color-dot" style={{background: '#2980B9'}}></span></button>
                                                                                    <button type="button" className="template-format-btn template-color-btn" onClick={() => applyFormat('foreColor', '#F1C40F', snippetRefs.current[`${section.title}_${snippet.id}`])} title="Yellow"><span className="template-color-dot" style={{background: '#F1C40F'}}></span></button>
                                                                                    <button type="button" className="template-format-btn template-color-btn" onClick={() => applyFormat('foreColor', '#E74C3C', snippetRefs.current[`${section.title}_${snippet.id}`])} title="Red"><span className="template-color-dot" style={{background: '#E74C3C'}}></span></button>
                                                                                    <button type="button" className="template-format-btn template-color-btn" onClick={() => applyFormat('foreColor', '#2E7D32', snippetRefs.current[`${section.title}_${snippet.id}`])} title="Green"><span className="template-color-dot" style={{background: '#2E7D32'}}></span></button>
                                                                                    <button type="button" className="template-format-btn template-color-btn" onClick={() => applyFormat('foreColor', '#000000', snippetRefs.current[`${section.title}_${snippet.id}`])} title="Black"><span className="template-color-dot" style={{background: '#000000'}}></span></button>
                                                                                    <button type="button" className="template-format-btn" onClick={() => applyFormat('removeFormat', null, snippetRefs.current[`${section.title}_${snippet.id}`])} title="Clear"><BsXLg size={8} /></button>
                                                                                    <span className="template-format-divider"></span>
                                                                                    <button type="button" className="template-format-btn" onClick={() => applyFormat('insertUnorderedList', null, snippetRefs.current[`${section.title}_${snippet.id}`])} title="Bullet list"><BsListUl size={11} /></button>
                                                                                    <button type="button" className="template-format-btn" onClick={() => applyFormat('outdent', null, snippetRefs.current[`${section.title}_${snippet.id}`])} title="Decrease indent"><BsTextIndentRight size={11} /></button>
                                                                                    <button type="button" className="template-format-btn" onClick={() => applyFormat('indent', null, snippetRefs.current[`${section.title}_${snippet.id}`])} title="Increase indent"><BsTextIndentLeft size={11} /></button>
                                                                                </div>
                                                                                <div
                                                                                    ref={el => { snippetRefs.current[`${section.title}_${snippet.id}`] = el; }}
                                                                                    className="snippet-form-textarea template-richtext snippet-popout-editor snippet-compact-editor"
                                                                                    contentEditable
                                                                                    suppressContentEditableWarning
                                                                                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(snippet.text || '') }}
                                                                                    onPaste={e => handleEditorPaste(e)}
                                                                                    onDrop={e => handleEditorDrop(e)}
                                                                                    onDragOver={e => { e.preventDefault(); e.stopPropagation(); }}
                                                                                    onKeyDown={e => {
                                                                                        if (e.key === 'Tab') {
                                                                                            e.preventDefault();
                                                                                            const sel = window.getSelection();
                                                                                            let node = sel && sel.anchorNode;
                                                                                            let inList = false;
                                                                                            while (node && node !== e.currentTarget) {
                                                                                                if (node.nodeName === 'LI') { inList = true; break; }
                                                                                                node = node.parentNode;
                                                                                            }
                                                                                            if (!inList) {
                                                                                                document.execCommand('insertUnorderedList', false, null);
                                                                                            } else {
                                                                                                document.execCommand(e.shiftKey ? 'outdent' : 'indent', false, null);
                                                                                            }
                                                                                        }
                                                                                    }}
                                                                                    onBlur={async () => {
                                                                                        const el = snippetRefs.current[`${section.title}_${snippet.id}`];
                                                                                        if (!el) return;
                                                                                        const html = DOMPurify.sanitize(el.innerHTML);
                                                                                        const current = sectionSnippets[section.title] || [];
                                                                                        const updated = current.map(s => s.id === snippet.id ? { ...s, text: html, objectiveText: html } : s);
                                                                                        const updatedMap = { ...sectionSnippets, [section.title]: updated };
                                                                                        saveSectionSnippets(updatedMap);
                                                                                    }}
                                                                                />
                                                                                <label className="snippet-field-label">Impression</label>
                                                                                <div className="template-format-toolbar snippet-popout-toolbar" onMouseDown={e => e.preventDefault()}>
                                                                                    <button type="button" className="template-format-btn" onClick={() => applyFormat('bold', null, snippetRefs.current[`${section.title}_${snippet.id}_imp`])} title="Bold"><BsTypeBold size={11} /></button>
                                                                                    <button type="button" className="template-format-btn" onClick={() => applyFormat('italic', null, snippetRefs.current[`${section.title}_${snippet.id}_imp`])} title="Italic"><BsTypeItalic size={11} /></button>
                                                                                    <button type="button" className="template-format-btn" onClick={() => applyFormat('underline', null, snippetRefs.current[`${section.title}_${snippet.id}_imp`])} title="Underline"><BsTypeUnderline size={11} /></button>
                                                                                    <span className="template-format-divider"></span>
                                                                                    <button type="button" className="template-format-btn template-color-btn" onClick={() => applyFormat('foreColor', '#E67E22', snippetRefs.current[`${section.title}_${snippet.id}_imp`])} title="Orange"><span className="template-color-dot" style={{background: '#E67E22'}}></span></button>
                                                                                    <button type="button" className="template-format-btn template-color-btn" onClick={() => applyFormat('foreColor', '#2980B9', snippetRefs.current[`${section.title}_${snippet.id}_imp`])} title="Blue"><span className="template-color-dot" style={{background: '#2980B9'}}></span></button>
                                                                                    <button type="button" className="template-format-btn template-color-btn" onClick={() => applyFormat('foreColor', '#F1C40F', snippetRefs.current[`${section.title}_${snippet.id}_imp`])} title="Yellow"><span className="template-color-dot" style={{background: '#F1C40F'}}></span></button>
                                                                                    <button type="button" className="template-format-btn template-color-btn" onClick={() => applyFormat('foreColor', '#E74C3C', snippetRefs.current[`${section.title}_${snippet.id}_imp`])} title="Red"><span className="template-color-dot" style={{background: '#E74C3C'}}></span></button>
                                                                                    <button type="button" className="template-format-btn template-color-btn" onClick={() => applyFormat('foreColor', '#2E7D32', snippetRefs.current[`${section.title}_${snippet.id}_imp`])} title="Green"><span className="template-color-dot" style={{background: '#2E7D32'}}></span></button>
                                                                                    <button type="button" className="template-format-btn template-color-btn" onClick={() => applyFormat('foreColor', '#000000', snippetRefs.current[`${section.title}_${snippet.id}_imp`])} title="Black"><span className="template-color-dot" style={{background: '#000000'}}></span></button>
                                                                                    <button type="button" className="template-format-btn" onClick={() => applyFormat('removeFormat', null, snippetRefs.current[`${section.title}_${snippet.id}_imp`])} title="Clear"><BsXLg size={8} /></button>
                                                                                    <span className="template-format-divider"></span>
                                                                                    <button type="button" className="template-format-btn" onClick={() => applyFormat('insertUnorderedList', null, snippetRefs.current[`${section.title}_${snippet.id}_imp`])} title="Bullet list"><BsListUl size={11} /></button>
                                                                                    <button type="button" className="template-format-btn" onClick={() => applyFormat('outdent', null, snippetRefs.current[`${section.title}_${snippet.id}_imp`])} title="Decrease indent"><BsTextIndentRight size={11} /></button>
                                                                                    <button type="button" className="template-format-btn" onClick={() => applyFormat('indent', null, snippetRefs.current[`${section.title}_${snippet.id}_imp`])} title="Increase indent"><BsTextIndentLeft size={11} /></button>
                                                                                </div>
                                                                                <div
                                                                                    ref={el => { snippetRefs.current[`${section.title}_${snippet.id}_imp`] = el; }}
                                                                                    className="snippet-form-textarea template-richtext snippet-popout-editor snippet-compact-editor"
                                                                                    contentEditable
                                                                                    suppressContentEditableWarning
                                                                                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(snippet.impressionText || '') }}
                                                                                    onPaste={e => handleEditorPaste(e)}
                                                                                    onDrop={e => handleEditorDrop(e)}
                                                                                    onDragOver={e => { e.preventDefault(); e.stopPropagation(); }}
                                                                                    data-placeholder="Impression text"
                                                                                    onKeyDown={e => {
                                                                                        if (e.key === 'Tab') {
                                                                                            e.preventDefault();
                                                                                            const sel = window.getSelection();
                                                                                            let node = sel && sel.anchorNode;
                                                                                            let inList = false;
                                                                                            while (node && node !== e.currentTarget) {
                                                                                                if (node.nodeName === 'LI') { inList = true; break; }
                                                                                                node = node.parentNode;
                                                                                            }
                                                                                            if (!inList) {
                                                                                                document.execCommand('insertUnorderedList', false, null);
                                                                                            } else {
                                                                                                document.execCommand(e.shiftKey ? 'outdent' : 'indent', false, null);
                                                                                            }
                                                                                        }
                                                                                    }}
                                                                                    onBlur={async () => {
                                                                                        const el = snippetRefs.current[`${section.title}_${snippet.id}_imp`];
                                                                                        if (!el) return;
                                                                                        const html = DOMPurify.sanitize(el.innerHTML);
                                                                                        const current = sectionSnippets[section.title] || [];
                                                                                        const updated = current.map(s => s.id === snippet.id ? { ...s, impressionText: html } : s);
                                                                                        const updatedMap = { ...sectionSnippets, [section.title]: updated };
                                                                                        saveSectionSnippets(updatedMap);
                                                                                    }}
                                                                                />
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                    );
                                                                })}
                                                                        </div>
                                                                    );
                                                                    });
                                                                })()}
                                                                {sectionSnippetForm && sectionSnippetForm.sectionTitle === section.title ? (
                                                                    <div className="snippet-form snippet-form-compact">
                                                                        <input
                                                                            className="snippet-form-input"
                                                                            type="text"
                                                                            placeholder="Snippet title"
                                                                            value={sectionSnippetForm.label}
                                                                            onChange={e => setSectionSnippetForm(prev => ({ ...prev, label: e.target.value }))}
                                                                            autoFocus
                                                                        />
                                                                        <input
                                                                            className="snippet-form-input"
                                                                            type="text"
                                                                            placeholder="Category (optional)"
                                                                            value={sectionSnippetForm.category || ''}
                                                                            onChange={e => setSectionSnippetForm(prev => ({ ...prev, category: e.target.value }))}
                                                                            list={`snippet-categories-${section.id}`}
                                                                        />
                                                                        <datalist id={`snippet-categories-${section.id}`}>
                                                                            {[...new Set((sectionSnippets[section.title] || []).map(s => (s.category || '').trim()).filter(Boolean))].map(cat => (
                                                                                <option key={cat} value={cat} />
                                                                            ))}
                                                                        </datalist>
                                                                        <label className="snippet-field-label">Objective</label>
                                                                        <div className="template-format-toolbar snippet-popout-toolbar" onMouseDown={e => e.preventDefault()}>
                                                                            <button type="button" className="template-format-btn" onClick={() => applyFormat('bold', null, snippetEditorRef.current)} title="Bold"><BsTypeBold size={11} /></button>
                                                                            <button type="button" className="template-format-btn" onClick={() => applyFormat('italic', null, snippetEditorRef.current)} title="Italic"><BsTypeItalic size={11} /></button>
                                                                            <button type="button" className="template-format-btn" onClick={() => applyFormat('underline', null, snippetEditorRef.current)} title="Underline"><BsTypeUnderline size={11} /></button>
                                                                            <span className="template-format-divider"></span>
                                                                            <button type="button" className="template-format-btn template-color-btn" onClick={() => applyFormat('foreColor', '#E67E22', snippetEditorRef.current)} title="Orange"><span className="template-color-dot" style={{background: '#E67E22'}}></span></button>
                                                                            <button type="button" className="template-format-btn template-color-btn" onClick={() => applyFormat('foreColor', '#2980B9', snippetEditorRef.current)} title="Blue"><span className="template-color-dot" style={{background: '#2980B9'}}></span></button>
                                                                            <button type="button" className="template-format-btn template-color-btn" onClick={() => applyFormat('foreColor', '#F1C40F', snippetEditorRef.current)} title="Yellow"><span className="template-color-dot" style={{background: '#F1C40F'}}></span></button>
                                                                            <button type="button" className="template-format-btn template-color-btn" onClick={() => applyFormat('foreColor', '#E74C3C', snippetEditorRef.current)} title="Red"><span className="template-color-dot" style={{background: '#E74C3C'}}></span></button>
                                                                            <button type="button" className="template-format-btn template-color-btn" onClick={() => applyFormat('foreColor', '#2E7D32', snippetEditorRef.current)} title="Green"><span className="template-color-dot" style={{background: '#2E7D32'}}></span></button>
                                                                            <button type="button" className="template-format-btn template-color-btn" onClick={() => applyFormat('foreColor', '#000000', snippetEditorRef.current)} title="Black"><span className="template-color-dot" style={{background: '#000000'}}></span></button>
                                                                            <button type="button" className="template-format-btn" onClick={() => applyFormat('removeFormat', null, snippetEditorRef.current)} title="Clear"><BsXLg size={8} /></button>
                                                                            <span className="template-format-divider"></span>
                                                                            <button type="button" className="template-format-btn" onClick={() => applyFormat('insertUnorderedList', null, snippetEditorRef.current)} title="Bullet list"><BsListUl size={11} /></button>
                                                                            <button type="button" className="template-format-btn" onClick={() => applyFormat('outdent', null, snippetEditorRef.current)} title="Decrease indent"><BsTextIndentRight size={11} /></button>
                                                                            <button type="button" className="template-format-btn" onClick={() => applyFormat('indent', null, snippetEditorRef.current)} title="Increase indent"><BsTextIndentLeft size={11} /></button>
                                                                        </div>
                                                                        <div
                                                                            ref={snippetEditorRef}
                                                                            className="snippet-form-textarea template-richtext snippet-popout-editor snippet-compact-editor"
                                                                            contentEditable
                                                                            suppressContentEditableWarning
                                                                            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(sectionSnippetForm.objectiveText || '') }}
                                                                            onPaste={e => handleEditorPaste(e)}
                                                                            onDrop={e => handleEditorDrop(e)}
                                                                            onDragOver={e => { e.preventDefault(); e.stopPropagation(); }}
                                                                            onKeyDown={e => {
                                                                                if (e.key === 'Tab') {
                                                                                    e.preventDefault();
                                                                                    const sel = window.getSelection();
                                                                                    let node = sel && sel.anchorNode;
                                                                                    let inList = false;
                                                                                    while (node && node !== e.currentTarget) {
                                                                                        if (node.nodeName === 'LI') { inList = true; break; }
                                                                                        node = node.parentNode;
                                                                                    }
                                                                                    if (!inList) {
                                                                                        document.execCommand('insertUnorderedList', false, null);
                                                                                    } else {
                                                                                        document.execCommand(e.shiftKey ? 'outdent' : 'indent', false, null);
                                                                                    }
                                                                                }
                                                                            }}
                                                                            data-placeholder="Objective findings text"
                                                                        />
                                                                        <label className="snippet-field-label">Impression</label>
                                                                        <div className="template-format-toolbar snippet-popout-toolbar" onMouseDown={e => e.preventDefault()}>
                                                                            <button type="button" className="template-format-btn" onClick={() => applyFormat('bold', null, snippetImpressionRef.current)} title="Bold"><BsTypeBold size={11} /></button>
                                                                            <button type="button" className="template-format-btn" onClick={() => applyFormat('italic', null, snippetImpressionRef.current)} title="Italic"><BsTypeItalic size={11} /></button>
                                                                            <button type="button" className="template-format-btn" onClick={() => applyFormat('underline', null, snippetImpressionRef.current)} title="Underline"><BsTypeUnderline size={11} /></button>
                                                                            <span className="template-format-divider"></span>
                                                                            <button type="button" className="template-format-btn template-color-btn" onClick={() => applyFormat('foreColor', '#E67E22', snippetImpressionRef.current)} title="Orange"><span className="template-color-dot" style={{background: '#E67E22'}}></span></button>
                                                                            <button type="button" className="template-format-btn template-color-btn" onClick={() => applyFormat('foreColor', '#2980B9', snippetImpressionRef.current)} title="Blue"><span className="template-color-dot" style={{background: '#2980B9'}}></span></button>
                                                                            <button type="button" className="template-format-btn template-color-btn" onClick={() => applyFormat('foreColor', '#F1C40F', snippetImpressionRef.current)} title="Yellow"><span className="template-color-dot" style={{background: '#F1C40F'}}></span></button>
                                                                            <button type="button" className="template-format-btn template-color-btn" onClick={() => applyFormat('foreColor', '#E74C3C', snippetImpressionRef.current)} title="Red"><span className="template-color-dot" style={{background: '#E74C3C'}}></span></button>
                                                                            <button type="button" className="template-format-btn template-color-btn" onClick={() => applyFormat('foreColor', '#2E7D32', snippetImpressionRef.current)} title="Green"><span className="template-color-dot" style={{background: '#2E7D32'}}></span></button>
                                                                            <button type="button" className="template-format-btn template-color-btn" onClick={() => applyFormat('foreColor', '#000000', snippetImpressionRef.current)} title="Black"><span className="template-color-dot" style={{background: '#000000'}}></span></button>
                                                                            <button type="button" className="template-format-btn" onClick={() => applyFormat('removeFormat', null, snippetImpressionRef.current)} title="Clear"><BsXLg size={8} /></button>
                                                                            <span className="template-format-divider"></span>
                                                                            <button type="button" className="template-format-btn" onClick={() => applyFormat('insertUnorderedList', null, snippetImpressionRef.current)} title="Bullet list"><BsListUl size={11} /></button>
                                                                            <button type="button" className="template-format-btn" onClick={() => applyFormat('outdent', null, snippetImpressionRef.current)} title="Decrease indent"><BsTextIndentRight size={11} /></button>
                                                                            <button type="button" className="template-format-btn" onClick={() => applyFormat('indent', null, snippetImpressionRef.current)} title="Increase indent"><BsTextIndentLeft size={11} /></button>
                                                                        </div>
                                                                        <div
                                                                            ref={snippetImpressionRef}
                                                                            className="snippet-form-textarea template-richtext snippet-popout-editor snippet-compact-editor"
                                                                            contentEditable
                                                                            suppressContentEditableWarning
                                                                            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(sectionSnippetForm.impressionText || '') }}
                                                                            onPaste={e => handleEditorPaste(e)}
                                                                            onDrop={e => handleEditorDrop(e)}
                                                                            onDragOver={e => { e.preventDefault(); e.stopPropagation(); }}
                                                                            onKeyDown={e => {
                                                                                if (e.key === 'Tab') {
                                                                                    e.preventDefault();
                                                                                    const sel = window.getSelection();
                                                                                    let node = sel && sel.anchorNode;
                                                                                    let inList = false;
                                                                                    while (node && node !== e.currentTarget) {
                                                                                        if (node.nodeName === 'LI') { inList = true; break; }
                                                                                        node = node.parentNode;
                                                                                    }
                                                                                    if (!inList) {
                                                                                        document.execCommand('insertUnorderedList', false, null);
                                                                                    } else {
                                                                                        document.execCommand(e.shiftKey ? 'outdent' : 'indent', false, null);
                                                                                    }
                                                                                }
                                                                            }}
                                                                            data-placeholder="Impression text"
                                                                        />
                                                                        <div className="snippet-form-actions">
                                                                            <button className="snippet-form-save" onClick={saveSectionSnippetForm} disabled={!sectionSnippetForm.label.trim()}>
                                                                                <BsCheck2 size={12} /> Save
                                                                            </button>
                                                                            <button className="snippet-form-cancel" onClick={() => setSectionSnippetForm(null)}>
                                                                                <BsXLg size={8} /> Cancel
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <button className="quick-text-add" onClick={() => addSectionSnippet(section.title)} style={{ marginTop: '4px' }}>
                                                                        <BsPlusCircle size={11} style={{ marginRight: '4px' }} />
                                                                        Add Snippet
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>

                                                </>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                            <button className="template-add-section-btn" onClick={addNewSection} title="Add a new, empty section to this report. Double-click a section title to rename it.">
                                <BsPlusCircle size={16} />
                                <span>Add Section</span>
                            </button>
                            <div className="template-footer-actions">
                                {patient?.finalDoc?.finalDoc && (
                                    <button
                                        className="template-record-load"
                                        onClick={handleLoadSavedReport}
                                        disabled={loadingSavedReport}
                                        title="Reload the last report saved to this record, discarding current unsaved edits"
                                    >
                                        {loadingSavedReport ? (
                                            <div className="template-spinner" style={{ width: '12px', height: '12px', borderWidth: '2px' }} />
                                        ) : (
                                            <BsFileEarmarkPdf size={13} />
                                        )}
                                        <span>Load Saved Report</span>
                                    </button>
                                )}
                                <button className="template-apply-btn" onClick={handleSaveReportToRecord} disabled={savingReport} title="Save this report (text + images) to the patient's record. You can reopen and edit it later.">
                                    {savingReport ? 'Saving Report…' : 'Save Report to Record'}
                                </button>
                                <button className="template-apply-btn" onClick={handleSaveTemplate} title="Save the current sections/text (no images) as a reusable template for future patients.">
                                    Apply Template
                                </button>
                                <button className="template-preview-btn" onClick={handlePreviewPdf} title="Open a PDF preview of the report in a new tab without saving.">
                                    <BsFileEarmarkText size={16} style={{marginRight: '6px'}} />
                                    Preview PDF
                                </button>
                                <button className="template-export-btn" onClick={handleExportPdf} title="Download the report as a PDF file to your computer.">
                                    <BsFileEarmarkPdf size={16} style={{marginRight: '6px'}} />
                                    Export PDF
                                </button>
                                <button className="template-back-btn template-cancel-x" onClick={() => navigate('/data')} title="Cancel — return to the dashboard. Unsaved changes will be lost." aria-label="Cancel">
                                    <BsXCircle size={18} />
                                </button>
                            </div>
                        </>
                    )}
                </div>

                {!loading && !loadError && (() => {
                    const masterImages = [];
                    sections.forEach(s => {
                        (s.images || []).forEach(img => masterImages.push({ img, section: s.title }));
                        if (s.imageSubsections) {
                            Object.keys(s.imageSubsections).forEach(sub => {
                                (s.imageSubsections[sub] || []).forEach(img => masterImages.push({ img, section: `${s.title} · ${sub}` }));
                            });
                        }
                    });
                    return imageMasterCollapsed ? (
                        <button
                            className="image-master-tab"
                            onClick={() => setImageMasterCollapsed(false)}
                            title="Show all images"
                        >
                            <BsImage size={14} />
                            {masterImages.length > 0 && <span className="image-master-tab-count">{masterImages.length}</span>}
                        </button>
                    ) : (
                        <div className="image-master-popout">
                            <div className="section-clipboard-header">
                                <span className="section-clipboard-title">
                                    <BsImage size={13} style={{ marginRight: '6px' }} />
                                    All Images ({masterImages.length})
                                </span>
                                <button className="section-clipboard-close" onClick={() => setImageMasterCollapsed(true)} title="Hide">
                                    <BsChevronRight size={12} />
                                </button>
                            </div>
                            <div className="section-clipboard-body image-master-body">
                                {masterImages.length === 0 ? (
                                    <div className="image-master-empty">
                                        <BsImage size={24} />
                                        <span>No images yet.</span>
                                        <span className="image-master-empty-hint">Paste or attach images in a section and they appear here.</span>
                                    </div>
                                ) : (
                                    <div className="image-master-grid">
                                        {masterImages.map(({ img, section }) => (
                                            <div key={img.id} className="image-master-item" onClick={() => openLightbox(img)} title={`${section} — click to open`}>
                                                <img src={img.src} alt={img.name} className="image-master-thumb" draggable={false} />
                                                <span className="image-master-label">{section}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })()}

                {/* Toolkit panel removed — individual section snippets are kept */}
                {false && <div className={`quick-text-panel${quickTextOpen ? ' quick-text-open' : ''}`}>
                    <button className="quick-text-toggle" onClick={() => setQuickTextOpen(prev => !prev)}>
                        {quickTextOpen ? 'Toolkit \u25B6' : '\u25C0 Toolkit'}
                    </button>
                    <div className="quick-text-content">
                        <div className="quick-panel-tabs">
                            <button
                                className={`quick-panel-tab${quickPanelTab === 'snippets' ? ' quick-panel-tab-active' : ''}`}
                                onClick={() => setQuickPanelTab('snippets')}
                            >
                                <BsClipboard size={13} /> Snippets
                            </button>
                            <button
                                className={`quick-panel-tab${quickPanelTab === 'images' ? ' quick-panel-tab-active' : ''}`}
                                onClick={() => setQuickPanelTab('images')}
                            >
                                <BsImage size={13} /> Images
                            </button>
                        </div>

                        {quickPanelTab === 'snippets' && (
                            <div className="quick-panel-tab-content">
                                {/* Global Snippets */}
                                <div className="master-section-group">
                                    <div className="master-section-title">Global Snippets</div>
                                    {quickSnippets.length > 0 ? (
                                        <div className="quick-text-snippets" onMouseDown={e => e.preventDefault()}>
                                            {quickSnippets.map(snippet => (
                                                <div
                                                    key={snippet.id}
                                                    className="quick-text-snippet-card quick-text-snippet-card-clickable"
                                                    onClick={() => insertSnippet(snippet.text)}
                                                    title="Insert into focused editor"
                                                >
                                                    <div className="quick-text-snippet-header">
                                                        <span className="quick-text-snippet-label">{snippet.label}</span>
                                                        <div className="quick-text-snippet-actions">
                                                            <button
                                                                className="quick-text-snippet-btn-insert"
                                                                onClick={(e) => { e.stopPropagation(); editSnippet(snippet.id); }}
                                                                title="Edit snippet"
                                                            >
                                                                Edit
                                                            </button>
                                                            <button
                                                                className="quick-text-snippet-remove"
                                                                onClick={(e) => { e.stopPropagation(); removeSnippet(snippet.id); }}
                                                                title="Remove snippet"
                                                            >
                                                                <BsXLg size={8} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                    <div className="snippet-preview" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(snippet.text || '') }} />
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="master-section-empty">No global snippets</p>
                                    )}
                                    {snippetForm ? (
                                        <div className="snippet-form">
                                            <input
                                                className="snippet-form-input"
                                                type="text"
                                                placeholder="Snippet name"
                                                value={snippetForm.label}
                                                onChange={e => setSnippetForm(prev => ({ ...prev, label: e.target.value }))}
                                                autoFocus
                                            />
                                            <div className="template-format-toolbar snippet-popout-toolbar" onMouseDown={e => e.preventDefault()}>
                                                <button type="button" className="template-format-btn" onClick={() => applyFormat('bold', null, snippetEditorRef.current)} title="Bold"><BsTypeBold size={11} /></button>
                                                <button type="button" className="template-format-btn" onClick={() => applyFormat('italic', null, snippetEditorRef.current)} title="Italic"><BsTypeItalic size={11} /></button>
                                                <button type="button" className="template-format-btn" onClick={() => applyFormat('underline', null, snippetEditorRef.current)} title="Underline"><BsTypeUnderline size={11} /></button>
                                                <span className="template-format-divider"></span>
                                                <button type="button" className="template-format-btn template-color-btn" onClick={() => applyFormat('foreColor', '#E67E22', snippetEditorRef.current)} title="Orange"><span className="template-color-dot" style={{background: '#E67E22'}}></span></button>
                                                <button type="button" className="template-format-btn template-color-btn" onClick={() => applyFormat('foreColor', '#2980B9', snippetEditorRef.current)} title="Blue"><span className="template-color-dot" style={{background: '#2980B9'}}></span></button>
                                                                            <button type="button" className="template-format-btn template-color-btn" onClick={() => applyFormat('foreColor', '#F1C40F', snippetEditorRef.current)} title="Yellow"><span className="template-color-dot" style={{background: '#F1C40F'}}></span></button>
                                                <button type="button" className="template-format-btn template-color-btn" onClick={() => applyFormat('foreColor', '#E74C3C', snippetEditorRef.current)} title="Red"><span className="template-color-dot" style={{background: '#E74C3C'}}></span></button>
                                                <button type="button" className="template-format-btn template-color-btn" onClick={() => applyFormat('foreColor', '#2E7D32', snippetEditorRef.current)} title="Green"><span className="template-color-dot" style={{background: '#2E7D32'}}></span></button>
                                                                            <button type="button" className="template-format-btn template-color-btn" onClick={() => applyFormat('foreColor', '#000000', snippetEditorRef.current)} title="Black"><span className="template-color-dot" style={{background: '#000000'}}></span></button>
                                                <button type="button" className="template-format-btn" onClick={() => applyFormat('removeFormat', null, snippetEditorRef.current)} title="Clear"><BsXLg size={8} /></button>
                                                <span className="template-format-divider"></span>
                                                <button type="button" className="template-format-btn" onClick={() => applyFormat('insertUnorderedList', null, snippetEditorRef.current)} title="Bullet list"><BsListUl size={11} /></button>
                                                <button type="button" className="template-format-btn" onClick={() => applyFormat('insertOrderedList', null, snippetEditorRef.current)} title="Numbered list"><BsListOl size={11} /></button>
                                                <button type="button" className="template-format-btn" onClick={() => applyFormat('outdent', null, snippetEditorRef.current)} title="Decrease indent"><BsTextIndentRight size={11} /></button>
                                                <button type="button" className="template-format-btn" onClick={() => applyFormat('indent', null, snippetEditorRef.current)} title="Increase indent"><BsTextIndentLeft size={11} /></button>
                                                <span className="template-format-divider"></span>
                                                <button type="button" className="template-format-btn" onClick={() => applyFormat('undo', null, snippetEditorRef.current)} title="Undo"><BsArrowCounterclockwise size={10} /></button>
                                                <button type="button" className="template-format-btn" onClick={() => applyFormat('redo', null, snippetEditorRef.current)} title="Redo"><BsArrowClockwise size={10} /></button>
                                            </div>
                                            <div
                                                ref={snippetEditorRef}
                                                className="snippet-form-textarea template-richtext snippet-popout-editor"
                                                contentEditable
                                                suppressContentEditableWarning
                                                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(snippetForm.text || '') }}
                                                onPaste={e => handleEditorPaste(e)}
                                                onDrop={e => handleEditorDrop(e)}
                                                onDragOver={e => { e.preventDefault(); e.stopPropagation(); }}
                                                data-placeholder="Snippet text"
                                            />
                                            <div className="snippet-form-actions">
                                                <button className="snippet-form-save" onClick={saveSnippetForm} disabled={!snippetForm.label.trim()}>
                                                    <BsCheck2 size={12} /> Save
                                                </button>
                                                <button className="snippet-form-cancel" onClick={() => setSnippetForm(null)}>
                                                    <BsXLg size={8} /> Cancel
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <button className="quick-text-add" onClick={addSnippet} style={{ marginTop: '8px' }}>
                                            <BsPlusCircle size={12} style={{ marginRight: '4px' }} />
                                            Add Global Snippet
                                        </button>
                                    )}
                                </div>

                                {/* Section Snippets */}
                                <p className="quick-text-desc" style={{ marginTop: '14px' }}>Section snippets</p>
                                {sections.filter(s => s.enabled).map(section => {
                                    const secSnippets = sectionSnippets[section.title] || [];
                                    return (
                                        <div key={section.id} className="master-section-group">
                                            <div className="master-section-title">{section.title}</div>
                                            {secSnippets.length > 0 ? (
                                                <div className="quick-text-snippets" onMouseDown={e => e.preventDefault()}>
                                                    {secSnippets.map(snippet => (
                                                        <div
                                                            key={snippet.id}
                                                            className="quick-text-snippet-card quick-text-snippet-card-clickable"
                                                            onClick={() => {
                                                                document.execCommand('insertHTML', false, snippet.text);
                                                            }}
                                                            title={`Insert into ${section.title}`}
                                                        >
                                                            <div className="quick-text-snippet-header">
                                                                <span className="quick-text-snippet-label">{snippet.label}</span>
                                                                <div className="quick-text-snippet-actions">
                                                                    <button
                                                                        className="quick-text-snippet-remove"
                                                                        onClick={(e) => { e.stopPropagation(); removeSectionSnippet(section.title, snippet.id); }}
                                                                        title="Remove snippet"
                                                                    >
                                                                        <BsXLg size={8} />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                            <div className="snippet-preview" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(snippet.text || '') }} />
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <p className="master-section-empty">No snippets</p>
                                            )}
                                            {sectionSnippetForm && sectionSnippetForm.sectionTitle === section.title ? (
                                                <div className="snippet-form" style={{ marginTop: '8px' }}>
                                                    <input
                                                        className="snippet-form-input"
                                                        type="text"
                                                        placeholder="Snippet name"
                                                        value={sectionSnippetForm.label}
                                                        onChange={e => setSectionSnippetForm(prev => ({ ...prev, label: e.target.value }))}
                                                        autoFocus
                                                    />
                                                    <div className="template-format-toolbar snippet-popout-toolbar" onMouseDown={e => e.preventDefault()}>
                                                        <button type="button" className="template-format-btn" onClick={() => applyFormat('bold', null, snippetEditorRef.current)} title="Bold"><BsTypeBold size={11} /></button>
                                                        <button type="button" className="template-format-btn" onClick={() => applyFormat('italic', null, snippetEditorRef.current)} title="Italic"><BsTypeItalic size={11} /></button>
                                                        <button type="button" className="template-format-btn" onClick={() => applyFormat('underline', null, snippetEditorRef.current)} title="Underline"><BsTypeUnderline size={11} /></button>
                                                        <span className="template-format-divider"></span>
                                                        <button type="button" className="template-format-btn template-color-btn" onClick={() => applyFormat('foreColor', '#E67E22', snippetEditorRef.current)} title="Orange"><span className="template-color-dot" style={{background: '#E67E22'}}></span></button>
                                                        <button type="button" className="template-format-btn template-color-btn" onClick={() => applyFormat('foreColor', '#2980B9', snippetEditorRef.current)} title="Blue"><span className="template-color-dot" style={{background: '#2980B9'}}></span></button>
                                                                            <button type="button" className="template-format-btn template-color-btn" onClick={() => applyFormat('foreColor', '#F1C40F', snippetEditorRef.current)} title="Yellow"><span className="template-color-dot" style={{background: '#F1C40F'}}></span></button>
                                                        <button type="button" className="template-format-btn template-color-btn" onClick={() => applyFormat('foreColor', '#E74C3C', snippetEditorRef.current)} title="Red"><span className="template-color-dot" style={{background: '#E74C3C'}}></span></button>
                                                        <button type="button" className="template-format-btn" onClick={() => applyFormat('removeFormat', null, snippetEditorRef.current)} title="Clear"><BsXLg size={8} /></button>
                                                        <span className="template-format-divider"></span>
                                                        <button type="button" className="template-format-btn" onClick={() => applyFormat('insertUnorderedList', null, snippetEditorRef.current)} title="Bullet list"><BsListUl size={11} /></button>
                                                        <button type="button" className="template-format-btn" onClick={() => applyFormat('insertOrderedList', null, snippetEditorRef.current)} title="Numbered list"><BsListOl size={11} /></button>
                                                        <button type="button" className="template-format-btn" onClick={() => applyFormat('outdent', null, snippetEditorRef.current)} title="Decrease indent"><BsTextIndentRight size={11} /></button>
                                                        <button type="button" className="template-format-btn" onClick={() => applyFormat('indent', null, snippetEditorRef.current)} title="Increase indent"><BsTextIndentLeft size={11} /></button>
                                                        <span className="template-format-divider"></span>
                                                        <button type="button" className="template-format-btn" onClick={() => applyFormat('undo', null, snippetEditorRef.current)} title="Undo"><BsArrowCounterclockwise size={10} /></button>
                                                        <button type="button" className="template-format-btn" onClick={() => applyFormat('redo', null, snippetEditorRef.current)} title="Redo"><BsArrowClockwise size={10} /></button>
                                                    </div>
                                                    <div
                                                        ref={snippetEditorRef}
                                                        className="snippet-form-textarea template-richtext snippet-popout-editor"
                                                        contentEditable
                                                        suppressContentEditableWarning
                                                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(sectionSnippetForm.text || '') }}
                                                        onPaste={e => handleEditorPaste(e)}
                                                        onDrop={e => handleEditorDrop(e)}
                                                        onDragOver={e => { e.preventDefault(); e.stopPropagation(); }}
                                                        data-placeholder="Snippet text"
                                                    />
                                                    <div className="snippet-form-actions">
                                                        <button className="snippet-form-save" onClick={saveSectionSnippetForm} disabled={!sectionSnippetForm.label.trim()}>
                                                            <BsCheck2 size={12} /> Save
                                                        </button>
                                                        <button className="snippet-form-cancel" onClick={() => setSectionSnippetForm(null)}>
                                                            <BsXLg size={8} /> Cancel
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <button className="quick-text-add" onClick={() => addSectionSnippet(section.title)} style={{ marginTop: '4px' }}>
                                                    <BsPlusCircle size={12} style={{ marginRight: '4px' }} />
                                                    Add Snippet
                                                </button>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {quickPanelTab === 'images' && (
                            <div
                                className="quick-panel-tab-content image-bank-surface"
                                onPaste={handleImageBankPaste}
                                onDrop={handleImageBankDrop}
                                onDragOver={e => { e.preventDefault(); e.stopPropagation(); }}
                                tabIndex={0}
                            >
                                {imageBank.length === 0 ? (
                                    <div className="image-bank-empty">
                                        <BsImage size={28} />
                                        <span className="image-bank-empty-title">Paste to add</span>
                                        <span className="image-bank-empty-hint">Snip from DICOM viewer, then Ctrl+V / Cmd+V anywhere here</span>
                                        <button
                                            className="image-bank-browse-btn"
                                            onClick={() => imageBankInputRef.current?.click()}
                                        >
                                            or browse files
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        <div className="image-bank-grid">
                                            {imageBank.map(img => (
                                                <div
                                                    key={img.id}
                                                    className="image-bank-thumb image-bank-thumb-clickable"
                                                    onClick={() => insertBankImageToSection(img)}
                                                    title="Click to insert into section"
                                                >
                                                    <img src={img.src} alt={img.name} />
                                                    <button
                                                        className="image-bank-remove"
                                                        onClick={(e) => { e.stopPropagation(); removeFromImageBank(img.id); }}
                                                        title="Remove"
                                                    >
                                                        <BsXLg size={8} />
                                                    </button>
                                                </div>
                                            ))}
                                            <div
                                                className="image-bank-thumb image-bank-add-more"
                                                onClick={() => imageBankInputRef.current?.click()}
                                                title="Add more images"
                                            >
                                                <BsPlusCircle size={20} />
                                            </div>
                                        </div>
                                        <p className="image-bank-tip">Click image to insert into section. Paste to add more.</p>
                                    </>
                                )}
                                <input
                                    ref={imageBankInputRef}
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    style={{ display: 'none' }}
                                    onChange={e => { addToImageBank(e.target.files); e.target.value = ''; }}
                                />
                            </div>
                        )}
                    </div>
                </div>}
            </div>

            {lightbox && (
                <div className="image-lightbox-overlay" onClick={closeLightbox}>
                    <div className="image-lightbox-toolbar" onClick={e => e.stopPropagation()}>
                        <button className="image-lightbox-btn" onClick={() => zoomAt(1 / 1.15)} title="Zoom out">
                            <BsZoomOut size={16} />
                        </button>
                        <span className="image-lightbox-zoom-label">{Math.round(lightboxZoom * 100)}%</span>
                        <button className="image-lightbox-btn" onClick={() => zoomAt(1.15)} title="Zoom in">
                            <BsZoomIn size={16} />
                        </button>
                        <button className="image-lightbox-btn" onClick={() => { setLightboxZoom(1); setLightboxPan({ x: 0, y: 0 }); }} title="Reset">
                            <BsArrowCounterclockwise size={15} />
                        </button>
                        <button className="image-lightbox-btn" onClick={closeLightbox} title="Close">
                            <BsXLg size={14} />
                        </button>
                    </div>
                    <div
                        className="image-lightbox-stage"
                        ref={lightboxStageRef}
                        onClick={e => e.stopPropagation()}
                        onMouseDown={handleLightboxPanStart}
                    >
                        <img
                            src={lightbox.src}
                            alt={lightbox.name}
                            className="image-lightbox-img"
                            style={{ transform: `translate(${lightboxPan.x}px, ${lightboxPan.y}px) scale(${lightboxZoom})` }}
                            draggable={false}
                            onLoad={e => {
                                const stage = lightboxStageRef.current;
                                if (!stage) return;
                                setLightboxPan({
                                    x: (stage.clientWidth - e.target.offsetWidth) / 2,
                                    y: (stage.clientHeight - e.target.offsetHeight) / 2,
                                });
                            }}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}

export default TemplateCustomizer;
